import { useCallback, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Ban,
  BookOpen,
  CheckCircle2,
  Clock3,
  Gauge,
  LineChart,
  Loader2,
  Lamp,
  Mail,
  MessageSquare,
  PiggyBank,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Download,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import DashboardLayoutV3 from "@/components/DashboardLayout.V3";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import AutopilotOnboardingWizard from "@/features/autopilot/components/AutopilotOnboardingWizard";
import AutopilotNotificationCreditsCard from "@/features/autopilot/components/AutopilotNotificationCreditsCard";
import AutopilotConnectorCredentialDialog from "@/features/autopilot/components/AutopilotConnectorCredentialDialog";
import {
  useAutopilotConnectorDlqUploads,
  useAutopilotConnectorUploads,
  useAutopilotDiagnostics,
  useAutopilotSegmentationSnapshot,
  useTriggerAutopilotSegmentation,
  useAutopilotBudgetRecommendations,
  useUpdateAutopilotBudgetRecommendation,
  useAutopilotExperiments,
  useUpdateAutopilotExperiment,
  useAutopilotRollups,
  useDownloadAutopilotRollupsCsv,
  useAutopilotHeuristicsConfig,
  useUpdateAutopilotHeuristicsConfig,
  useAutopilotOptimizationAnalytics,
  useAutopilotOfferIntelligence,
  useAutopilotFranchiseAnalytics,
  useDownloadAutopilotFranchiseCsv,
} from "@/features/autopilot/hooks/useAutopilotDiagnostics";
import {
  enqueueAutopilotConnectorUpload,
  replayAutopilotConnectorUpload,
  setAutopilotConnectorAccountEnabled,
  type AutopilotConnectorAccount,
  type AutopilotConnectorDiagnostic,
  type AutopilotConnectorUpload,
} from "@/lib/api/autopilot";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import { cn } from "@/lib/utils";
import { useTenantSlug } from "@/stores/tenantStore";
import { useToast } from "@/hooks/use-toast";

type PipelineStatus = "healthy" | "monitoring" | "attention";

const statusStyles: Record<PipelineStatus, string> = {
  healthy: "bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
  monitoring: "bg-amber-500/10 text-amber-600 ring-1 ring-inset ring-amber-500/40 dark:bg-amber-500/15 dark:text-amber-300",
  attention: "bg-rose-500/10 text-rose-600 ring-1 ring-inset ring-rose-500/40 dark:bg-rose-500/15 dark:text-rose-300",
};

const segmentationStatusStyles: Record<'queued' | 'running' | 'succeeded' | 'failed', string> = {
  queued: "bg-slate-500/10 text-slate-600 ring-1 ring-inset ring-slate-500/30 dark:bg-slate-500/15 dark:text-slate-200",
  running: "bg-amber-500/10 text-amber-600 ring-1 ring-inset ring-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200",
  succeeded: "bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
  failed: "bg-rose-500/10 text-rose-600 ring-1 ring-inset ring-rose-500/40 dark:bg-rose-500/15 dark:text-rose-300",
};

const budgetStatusStyles: Record<'pending' | 'scheduled' | 'applied' | 'dismissed' | 'snoozed', string> = {
  pending: "bg-slate-500/10 text-slate-600 ring-1 ring-inset ring-slate-500/30 dark:bg-slate-500/15 dark:text-slate-200",
  scheduled: "bg-amber-500/10 text-amber-600 ring-1 ring-inset ring-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200",
  applied: "bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
  dismissed: "bg-rose-500/10 text-rose-600 ring-1 ring-inset ring-rose-500/40 dark:bg-rose-500/15 dark:text-rose-300",
  snoozed: "bg-indigo-500/10 text-indigo-600 ring-1 ring-inset ring-indigo-500/40 dark:bg-indigo-500/15 dark:text-indigo-200",
};

const heuristicResultStyles: Record<'applied' | 'skipped' | 'blocked' | 'error', string> = {
  applied: "bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300",
  skipped: "bg-slate-500/10 text-slate-600 ring-1 ring-inset ring-slate-500/30 dark:bg-slate-500/15 dark:text-slate-200",
  blocked: "bg-amber-500/10 text-amber-600 ring-1 ring-inset ring-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200",
  error: "bg-rose-500/10 text-rose-600 ring-1 ring-inset ring-rose-500/40 dark:bg-rose-500/15 dark:text-rose-300",
};

const CONNECTOR_TYPES = [
  "google_ads",
  "meta_ads",
  "waze_ads",
  "nextdoor_ads",
  "sms_campaign",
  "email_campaign",
] as const satisfies ReadonlyArray<AutopilotConnectorDiagnostic["connector"]>;
type ConnectorType = (typeof CONNECTOR_TYPES)[number];

const channelBadgeStyles: Record<ConnectorType, string> = {
  google_ads: "bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-200",
  meta_ads: "bg-sky-500/10 text-sky-600 ring-1 ring-sky-500/30 dark:bg-sky-500/15 dark:text-sky-200",
  waze_ads: "bg-teal-500/10 text-teal-600 ring-1 ring-teal-500/30 dark:bg-teal-500/15 dark:text-teal-200",
  nextdoor_ads: "bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/40 dark:bg-emerald-500/20 dark:text-emerald-200",
  sms_campaign: "bg-indigo-500/10 text-indigo-600 ring-1 ring-indigo-500/30 dark:bg-indigo-500/15 dark:text-indigo-200",
  email_campaign: "bg-slate-500/10 text-slate-600 ring-1 ring-slate-500/30 dark:bg-slate-500/15 dark:text-slate-200",
};

const CONNECTOR_METRIC_COPY: Record<
  ConnectorType,
  {
    labelKey: string;
    trendKey: string;
    trendFallbackKey: string;
    defaultLabel: string;
    defaultTrend: string;
    defaultFallback: string;
    icon: LucideIcon;
  }
> = {
  google_ads: {
    labelKey: "prototype.metrics.matchRateGoogle.label",
    trendKey: "prototype.metrics.matchRateGoogle.trend",
    trendFallbackKey: "prototype.metrics.matchRateGoogle.trendFallback",
    defaultLabel: "Google match rate",
    defaultTrend: "Last success {{timeAgo}}",
    defaultFallback: "No sandbox uploads recorded",
    icon: ShieldCheck,
  },
  meta_ads: {
    labelKey: "prototype.metrics.matchRateMeta.label",
    trendKey: "prototype.metrics.matchRateMeta.trend",
    trendFallbackKey: "prototype.metrics.matchRateMeta.trendFallback",
    defaultLabel: "Meta match rate",
    defaultTrend: "Last success {{timeAgo}}",
    defaultFallback: "No sandbox uploads recorded",
    icon: LineChart,
  },
  waze_ads: {
    labelKey: "prototype.metrics.matchRateWaze.label",
    trendKey: "prototype.metrics.matchRateWaze.trend",
    trendFallbackKey: "prototype.metrics.matchRateWaze.trendFallback",
    defaultLabel: "Waze match rate",
    defaultTrend: "Last success {{timeAgo}}",
    defaultFallback: "No sandbox uploads recorded",
    icon: Gauge,
  },
  nextdoor_ads: {
    labelKey: "prototype.metrics.matchRateNextdoor.label",
    trendKey: "prototype.metrics.matchRateNextdoor.trend",
    trendFallbackKey: "prototype.metrics.matchRateNextdoor.trendFallback",
    defaultLabel: "Nextdoor match rate",
    defaultTrend: "Last success {{timeAgo}}",
    defaultFallback: "No sandbox uploads recorded",
    icon: Users,
  },
  sms_campaign: {
    labelKey: "prototype.metrics.matchRateSms.label",
    trendKey: "prototype.metrics.matchRateSms.trend",
    trendFallbackKey: "prototype.metrics.matchRateSms.trendFallback",
    defaultLabel: "SMS match rate",
    defaultTrend: "Last success {{timeAgo}}",
    defaultFallback: "No sandbox uploads recorded",
    icon: MessageSquare,
  },
  email_campaign: {
    labelKey: "prototype.metrics.matchRateEmail.label",
    trendKey: "prototype.metrics.matchRateEmail.trend",
    trendFallbackKey: "prototype.metrics.matchRateEmail.trendFallback",
    defaultLabel: "Email match rate",
    defaultTrend: "Last success {{timeAgo}}",
    defaultFallback: "No sandbox uploads recorded",
    icon: Mail,
  },
};

const CONNECTOR_LABEL_KEYS: Record<ConnectorType, string> = {
  google_ads: "prototype.connectors.google.label",
  meta_ads: "prototype.connectors.meta.label",
  waze_ads: "prototype.connectors.waze.label",
  nextdoor_ads: "prototype.connectors.nextdoor.label",
  sms_campaign: "prototype.connectors.sms.label",
  email_campaign: "prototype.connectors.email.label",
};

const CONNECTOR_LABEL_DEFAULTS: Record<ConnectorType, string> = {
  google_ads: "Google Ads",
  meta_ads: "Meta Ads",
  waze_ads: "Waze Ads",
  nextdoor_ads: "Nextdoor Ads",
  sms_campaign: "SMS Campaigns",
  email_campaign: "Email Campaigns",
};

type GuardrailStatus = "clear" | "degraded" | "paused";

const guardrailBadgeTone: Record<GuardrailStatus, string> = {
  clear: "bg-emerald-500/90 text-white",
  degraded: "bg-amber-500/90 text-amber-950 dark:text-amber-100",
  paused: "bg-slate-500/80 text-white",
};

type EducationPersona = "operator" | "manager";

type EducationEntry = {
  id: string;
  persona: EducationPersona;
  connector?: ConnectorType | null;
  severity: "info" | "warning";
  title: string;
  description: string;
  audience: string;
  ctaLabel?: string;
  href?: string;
  icon: LucideIcon;
};

type PlaybookHighlight = {
  id: string;
  title: string;
  description: string;
  audience: string;
  badgeLabel: string;
  badgeClassName: string;
};

const EDUCATION_DOC_LINKS: Record<string, string> = {
  general: "https://braisepos.com/docs/autopilot/education",
  "google_ads.activate": "https://braisepos.com/docs/autopilot/google-ads-sandbox",
  "google_ads.optimize": "https://braisepos.com/docs/autopilot/google-ads-match-rate",
  "meta_ads.activate": "https://braisepos.com/docs/autopilot/meta-capi-setup",
  "meta_ads.optimize": "https://braisepos.com/docs/autopilot/meta-optimizations",
  "waze_ads.activate": "https://braisepos.com/docs/autopilot/waze-sandbox",
  "waze_ads.optimize": "https://braisepos.com/docs/autopilot/waze-pins-quality",
  "nextdoor_ads.activate": "https://braisepos.com/docs/autopilot/nextdoor-sandbox",
  "nextdoor_ads.optimize": "https://braisepos.com/docs/autopilot/nextdoor-neighborhoods",
  "sms_campaign.activate": "https://braisepos.com/docs/autopilot/sms-consent",
  "sms_campaign.optimize": "https://braisepos.com/docs/autopilot/sms-cadence",
  "email_campaign.activate": "https://braisepos.com/docs/autopilot/email-auth",
  "email_campaign.optimize": "https://braisepos.com/docs/autopilot/email-deliverability",
};

const offerStateBadgeStyles: Record<'enabled' | 'disabled' | 'snoozed', string> = {
  enabled: "bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300",
  snoozed: "bg-amber-500/10 text-amber-600 ring-1 ring-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300",
  disabled: "bg-rose-500/10 text-rose-600 ring-1 ring-rose-500/30 dark:bg-rose-500/15 dark:text-rose-300",
};

type ConnectorTableRow = {
  id: string;
  batchId: string;
  connectorLabel?: string;
  status: PipelineStatus;
  statusText: string;
  conversions: string;
  matchRate: string;
  revenue: string;
  note: string;
  relativeTime: string;
  timestamp: string | null;
};

type ConnectorDlqRow = {
  id: string;
  batchId: string;
  rowsSummary: string;
  reason: string;
  relativeTime: string;
  timestamp: string | null;
  replayAttempt?: number;
};

type ConnectorTabConfig = {
  value: ConnectorType | "queue";
  label: string;
  health: string;
  status: PipelineStatus;
  uploads: ConnectorTableRow[];
  dlqUploads?: ConnectorDlqRow[];
  isConnector: boolean;
  enabled?: boolean;
  credentialStatus?: AutopilotConnectorDiagnostic["credentialStatus"];
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
  hasSandboxData?: boolean;
  totalUploads?: number;
  uploadsLoading?: boolean;
  uploadsRefreshing?: boolean;
};

const AutopilotPage = () => {
  const { t } = useTranslation(["autopilot", "common"]);
  const queryClient = useQueryClient();
  const {
    data: diagnostics = [],
    isLoading: diagnosticsLoading,
    isFetching: diagnosticsRefreshing,
    refetch: refetchDiagnostics,
  } = useAutopilotDiagnostics();
  const { formatNumber, formatPercent, formatCurrency, formatRelativeTime } = useLocaleFormatter();
  const translateConnectorLabel = useCallback(
    (connector: ConnectorType) =>
      t(CONNECTOR_LABEL_KEYS[connector], {
        defaultValue: CONNECTOR_LABEL_DEFAULTS[connector],
      }),
    [t],
  );
  const tenantSlug = useTenantSlug();
  const { toast } = useToast();
  const [credentialDialogConnector, setCredentialDialogConnector] = useState<ConnectorType | null>(null);
  const heuristicsToggleId = useId();
  const heuristicsToggleStatusId = `${heuristicsToggleId}-status`;
  const averageDlqRate =
    diagnostics.length > 0 ? diagnostics.reduce((acc, diag) => acc + diag.dlqRate, 0) / diagnostics.length : null;
  const {
    data: dlqUploads = [],
    isFetching: dlqRefreshing,
  } = useAutopilotConnectorDlqUploads({ environment: "sandbox", limit: 25 });
  const dlqByConnector = useMemo(() => {
    const map = new Map<string, AutopilotConnectorUpload[]>();
  dlqUploads.forEach((upload) => {
    const key = `${upload.connector}:${upload.environment}`;
    const existing = map.get(key);
    if (existing) {
      existing.push(upload);
      } else {
        map.set(key, [upload]);
      }
    });
    return map;
  }, [dlqUploads]);
  const {
    data: googleUploads = [],
    isFetching: googleUploadsRefreshing,
    isLoading: googleUploadsLoading,
    refetch: refetchGoogleUploads,
  } = useAutopilotConnectorUploads({ connector: "google_ads", environment: "sandbox", limit: 20 });
  const {
    data: metaUploads = [],
    isFetching: metaUploadsRefreshing,
    isLoading: metaUploadsLoading,
    refetch: refetchMetaUploads,
  } = useAutopilotConnectorUploads({ connector: "meta_ads", environment: "sandbox", limit: 20 });
  const {
    data: wazeUploads = [],
    isFetching: wazeUploadsRefreshing,
    isLoading: wazeUploadsLoading,
    refetch: refetchWazeUploads,
  } = useAutopilotConnectorUploads({ connector: "waze_ads", environment: "sandbox", limit: 20 });
  const {
    data: nextdoorUploads = [],
    isFetching: nextdoorUploadsRefreshing,
    isLoading: nextdoorUploadsLoading,
    refetch: refetchNextdoorUploads,
  } = useAutopilotConnectorUploads({ connector: "nextdoor_ads", environment: "sandbox", limit: 20 });
  const {
    data: smsUploads = [],
    isFetching: smsUploadsRefreshing,
    isLoading: smsUploadsLoading,
    refetch: refetchSmsUploads,
  } = useAutopilotConnectorUploads({ connector: "sms_campaign", environment: "sandbox", limit: 20 });
  const {
    data: emailUploads = [],
    isFetching: emailUploadsRefreshing,
    isLoading: emailUploadsLoading,
    refetch: refetchEmailUploads,
  } = useAutopilotConnectorUploads({ connector: "email_campaign", environment: "sandbox", limit: 20 });
  const {
    data: queueUploads = [],
    isFetching: queueUploadsRefreshing,
    isLoading: queueUploadsLoading,
    refetch: refetchQueueUploads,
  } = useAutopilotConnectorUploads({ environment: "sandbox", limit: 30 });
  const {
    data: budgetRecommendations = [],
    isLoading: budgetsLoading,
    isFetching: budgetsRefreshing,
    refetch: refetchBudgets,
  } = useAutopilotBudgetRecommendations();
  const {
    data: segmentationSnapshot,
    isLoading: segmentationLoading,
    isFetching: segmentationRefreshing,
    refetch: refetchSegmentation,
  } = useAutopilotSegmentationSnapshot();
  const triggerSegmentationMutation = useTriggerAutopilotSegmentation();
  const updateBudgetRecommendationMutation = useUpdateAutopilotBudgetRecommendation();
  const {
    data: autopilotExperiments = [],
    isLoading: experimentsLoading,
    isFetching: experimentsRefreshing,
    refetch: refetchExperiments,
  } = useAutopilotExperiments();
  const updateExperimentMutation = useUpdateAutopilotExperiment();
  const today = new Date();
  const startRangeDate = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
  const defaultEndDate = today.toISOString().slice(0, 10);
  const defaultStartDate = startRangeDate.toISOString().slice(0, 10);
  const {
    data: rollupData,
    isLoading: rollupsLoading,
    isFetching: rollupsRefreshing,
    refetch: refetchRollups,
  } = useAutopilotRollups({ startDate: defaultStartDate, endDate: defaultEndDate });
  const downloadRollupsMutation = useDownloadAutopilotRollupsCsv();
  const franchiseStartDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const franchiseEndDate = defaultEndDate;
  const {
    data: franchiseAnalyticsData,
    isLoading: franchiseLoading,
    isFetching: franchiseRefreshing,
    refetch: refetchFranchise,
  } = useAutopilotFranchiseAnalytics({ startDate: franchiseStartDate, endDate: franchiseEndDate });
  const downloadFranchiseMutation = useDownloadAutopilotFranchiseCsv();
  const {
    data: heuristicsData,
    isLoading: heuristicsLoading,
    isFetching: heuristicsRefreshing,
    refetch: refetchHeuristics,
  } = useAutopilotHeuristicsConfig();
  const updateHeuristicsConfigMutation = useUpdateAutopilotHeuristicsConfig();
  const {
    data: optimizationAnalyticsData,
    isLoading: optimizationLoading,
    isFetching: optimizationRefreshing,
    refetch: refetchOptimization,
  } = useAutopilotOptimizationAnalytics();
  const segmentationRun = segmentationSnapshot?.run ?? null;
  const segmentationRunTimestamp = segmentationRun?.completedAt ?? segmentationRun?.startedAt ?? null;
  const segmentationSegments = useMemo(
    () => segmentationSnapshot?.segments ?? [],
    [segmentationSnapshot],
  );
  const segmentationAudits = useMemo(
    () => segmentationSnapshot?.suppressionAudits ?? [],
    [segmentationSnapshot],
  );
  const hasSegmentationData = Boolean(segmentationRun) || segmentationSegments.length > 0;
  const hasBudgetRecommendations = budgetRecommendations.length > 0;
  const hasAutopilotExperiments = autopilotExperiments.length > 0;
  const rollupsSource = rollupData?.rollups;
  const rollups = useMemo(() => rollupsSource ?? [], [rollupsSource]);
  const rollupExports = rollupData?.exports ?? [];
  const latestRollupExport = rollupExports[0] ?? null;
  const totalRollupUploads = rollups.reduce((sum, entry) => sum + entry.totalUploads, 0);
  const totalSucceededRollupUploads = rollups.reduce((sum, entry) => sum + entry.succeededUploads, 0);
  const uploadSuccessRate =
    totalRollupUploads > 0 ? totalSucceededRollupUploads / totalRollupUploads : null;
  const avgRollupMatchRate =
    rollups.length > 0 ? rollups.reduce((sum, entry) => sum + entry.avgMatchRate, 0) / rollups.length : 0;
  const avgHoldoutPercent =
    rollups.length > 0 ? rollups.reduce((sum, entry) => sum + entry.holdoutPercent, 0) / rollups.length : 0;
  const budgetTotals = useMemo(() => {
    if (budgetRecommendations.length === 0) {
      return null;
    }
    return budgetRecommendations.reduce(
      (acc, entry) => {
        const rec = entry.recommendation;
        acc.target += Math.max(0, rec.recommendedBudgetCents ?? 0);
        const deployedValue =
          typeof rec.appliedBudgetCents === "number"
            ? rec.appliedBudgetCents
            : typeof rec.currentBudgetCents === "number"
              ? rec.currentBudgetCents
              : rec.recommendedBudgetCents ?? 0;
        acc.deployed += Math.max(0, deployedValue ?? 0);
        return acc;
      },
      { target: 0, deployed: 0 },
    );
  }, [budgetRecommendations]);
  const budgetTargetCents = budgetTotals && budgetTotals.target > 0 ? budgetTotals.target : null;
  const budgetDeployedCents = budgetTotals ? budgetTotals.deployed : null;
  const budgetDeploymentRatio =
    budgetTargetCents && budgetDeployedCents !== null && budgetTargetCents > 0
      ? Math.min(1, budgetDeployedCents / budgetTargetCents)
      : null;
  const budgetTargetDisplay =
    budgetTargetCents !== null ? formatCurrency(budgetTargetCents, undefined, { isMinorUnit: true }) : "--";
  const budgetDeployedDisplay =
    budgetDeployedCents !== null ? formatCurrency(budgetDeployedCents, undefined, { isMinorUnit: true }) : "--";
  const budgetDeploymentPercentLabel =
    budgetDeploymentRatio !== null ? formatPercent(budgetDeploymentRatio, { maximumFractionDigits: 0 }) : null;
  const budgetBadgeDisplay =
    budgetDeploymentPercentLabel !== null
      ? t("prototype.autopilot.budgetBadge", {
          defaultValue: "{{percent}} deployed",
          percent: budgetDeploymentPercentLabel,
        })
      : t("prototype.autopilot.budgetBadgePending", { defaultValue: "Awaiting spend" });
  const budgetProgressValue = budgetDeploymentRatio !== null ? budgetDeploymentRatio * 100 : 0;
  const holdoutSharePercent = Math.max(0, avgHoldoutPercent);
  const holdoutShareLabel =
    rollups.length > 0
      ? t("prototype.autopilot.holdoutShare", {
          defaultValue: "{{percent}} holdout share",
          percent: formatPercent(holdoutSharePercent / 100, { maximumFractionDigits: 0 }),
        })
      : t("prototype.autopilot.holdoutShareFallback", { defaultValue: "No holdout data yet" });
  const franchiseRollups = useMemo(
    () => franchiseAnalyticsData?.rollups ?? [],
    [franchiseAnalyticsData],
  );
  const franchiseWindow = franchiseAnalyticsData?.window ?? null;
  const franchiseSummary = useMemo(() => {
    if (franchiseRollups.length === 0) {
      return {
        totalUploads: 0,
        dlqRate: 0,
        trackedRevenueCents: 0,
        avgMatchRate: 0,
        avgHoldoutPercent: 0,
      };
    }
    const totals = franchiseRollups.reduce(
      (acc, entry) => {
        acc.totalUploads += entry.totalUploads;
        acc.dlqUploads += entry.dlqUploads;
        acc.revenue += entry.trackedRevenueCents;
        acc.matchRateSum += entry.avgMatchRate;
        acc.holdoutSum += entry.avgHoldoutPercent;
        acc.count += 1;
        return acc;
      },
      { totalUploads: 0, dlqUploads: 0, revenue: 0, matchRateSum: 0, holdoutSum: 0, count: 0 },
    );
    return {
      totalUploads: totals.totalUploads,
      dlqRate: totals.totalUploads > 0 ? totals.dlqUploads / totals.totalUploads : 0,
      trackedRevenueCents: totals.revenue,
      avgMatchRate: totals.count > 0 ? totals.matchRateSum / totals.count : 0,
      avgHoldoutPercent: totals.count > 0 ? totals.holdoutSum / totals.count : 0,
    };
  }, [franchiseRollups]);
  const totalProfilesScored = segmentationSegments.length;
  const aggregatedSegments = useMemo(() => {
    if (segmentationSegments.length === 0) {
      return [] as Array<{
        segment: string;
        count: number;
        share: number;
        suppressedCount: number;
        mismatchCount: number;
        avgRecencyDays: number;
        avgFrequencyScore: number;
        avgMonetaryScore: number;
      }>;
    }
    const map = new Map<
      string,
      {
        segment: string;
        count: number;
        suppressedCount: number;
        mismatchCount: number;
        recencyDaysTotal: number;
        frequencyScoreTotal: number;
        monetaryScoreTotal: number;
      }
    >();
    segmentationSegments.forEach((entry) => {
      const existing = map.get(entry.segment);
      if (existing) {
        existing.count += 1;
        existing.suppressedCount += entry.suppressed ? 1 : 0;
        existing.mismatchCount += entry.suppressionMismatch ? 1 : 0;
        existing.recencyDaysTotal += entry.recencyDays;
        existing.frequencyScoreTotal += entry.frequencyScore;
        existing.monetaryScoreTotal += entry.monetaryScore;
      } else {
        map.set(entry.segment, {
          segment: entry.segment,
          count: 1,
          suppressedCount: entry.suppressed ? 1 : 0,
          mismatchCount: entry.suppressionMismatch ? 1 : 0,
          recencyDaysTotal: entry.recencyDays,
          frequencyScoreTotal: entry.frequencyScore,
          monetaryScoreTotal: entry.monetaryScore,
        });
      }
    });
    return Array.from(map.values())
      .map((entry) => ({
        segment: entry.segment,
        count: entry.count,
        share: entry.count / segmentationSegments.length,
        suppressedCount: entry.suppressedCount,
        mismatchCount: entry.mismatchCount,
        avgRecencyDays: entry.recencyDaysTotal / entry.count,
        avgFrequencyScore: entry.frequencyScoreTotal / entry.count,
        avgMonetaryScore: entry.monetaryScoreTotal / entry.count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [segmentationSegments]);
  const topSegments = useMemo(() => aggregatedSegments.slice(0, 5), [aggregatedSegments]);
  const totalSuppressedProfiles = useMemo(
    () => aggregatedSegments.reduce((sum, entry) => sum + entry.suppressedCount, 0),
    [aggregatedSegments],
  );
  const totalMismatchProfiles = useMemo(
    () => aggregatedSegments.reduce((sum, entry) => sum + entry.mismatchCount, 0),
    [aggregatedSegments],
  );
  const suppressedShare = totalProfilesScored > 0 ? totalSuppressedProfiles / totalProfilesScored : 0;
  const mismatchShare = totalProfilesScored > 0 ? totalMismatchProfiles / totalProfilesScored : 0;
  const optInRate = totalProfilesScored > 0 ? Math.max(0, 1 - suppressedShare) : null;
  const optInPercentLabel =
    optInRate !== null
      ? formatPercent(optInRate, { maximumFractionDigits: 0 })
      : t('prototype.consent.rateFallback', { defaultValue: 'Awaiting data' });
  const optInProgressValue = optInRate !== null ? Math.round(optInRate * 100) : 0;
  const optInTrendLabel = segmentationRunTimestamp
    ? t('prototype.consent.lastRunLabel', {
        defaultValue: 'Last run {{timeAgo}}',
        timeAgo: formatRelativeTime(segmentationRunTimestamp),
      })
    : t('prototype.consent.lastRunFallback', { defaultValue: 'Awaiting segmentation run' });
  const suppressionAuditByChannel = useMemo(
    () =>
      [...segmentationAudits].sort((a, b) => a.channel.localeCompare(b.channel)),
    [segmentationAudits],
  );
  const mismatchChannelsWithIssues = useMemo(
    () => suppressionAuditByChannel.filter((audit) => audit.mismatchContacts > 0).length,
    [suppressionAuditByChannel],
  );
  const budgetActionLabels: Record<string, string> = {
    viewed: t("prototype.budgets.events.viewed", { defaultValue: "Viewed" }),
    accepted: t("prototype.budgets.events.accepted", { defaultValue: "Accepted" }),
    dismissed: t("prototype.budgets.events.dismissed", { defaultValue: "Dismissed" }),
    snoozed: t("prototype.budgets.events.snoozed", { defaultValue: "Snoozed" }),
    edited_budget: t("prototype.budgets.events.edited", { defaultValue: "Budget adjusted" }),
  };
  const formatBudgetCents = (value: number | null | undefined) =>
    formatCurrency(value ?? 0, undefined, { isMinorUnit: true });
  const budgetMutationPending = updateBudgetRecommendationMutation.isPending;
  const rollupMutationPending = downloadRollupsMutation.isPending;
  const heuristicsConfig = heuristicsData?.config ?? null;
  const heuristicsRules = heuristicsData?.rules ?? [];
  const heuristicsKillSwitchEnabled = heuristicsConfig?.killSwitchEnabled ?? true;
  const heuristicsActive = !heuristicsKillSwitchEnabled;
  const heuristicsStatusLabel = heuristicsActive
    ? t('prototype.optimization.killSwitch.on', { defaultValue: 'Automation active' })
    : t('prototype.optimization.killSwitch.off', { defaultValue: 'Paused' });
  const latestConnectorSyncAt = useMemo(() => {
    const timestamps: number[] = [];
    diagnostics.forEach((diag) => {
      if (diag.lastSuccessAt) {
        const parsed = Date.parse(diag.lastSuccessAt);
        if (Number.isFinite(parsed)) {
          timestamps.push(parsed);
        }
      }
    });
    rollups.forEach((entry) => {
      const parsed = Date.parse(entry.updatedAt ?? entry.createdAt);
      if (Number.isFinite(parsed)) {
        timestamps.push(parsed);
      }
    });
    if (latestRollupExport) {
      const parsed = Date.parse(latestRollupExport.updatedAt ?? latestRollupExport.createdAt);
      if (Number.isFinite(parsed)) {
        timestamps.push(parsed);
      }
    }
    if (timestamps.length === 0) {
      return null;
    }
    const latest = Math.max(...timestamps);
    return Number.isFinite(latest) ? new Date(latest).toISOString() : null;
  }, [diagnostics, latestRollupExport, rollups]);
  const heroSyncLabel = latestConnectorSyncAt
    ? t('prototype.hero.syncWindow', {
        defaultValue: 'Last sync {{timeAgo}}',
        timeAgo: formatRelativeTime(latestConnectorSyncAt),
      })
    : t('prototype.hero.syncFallback', { defaultValue: 'Awaiting first successful upload' });
  const totalConnectors = CONNECTOR_TYPES.length;
  const healthyConnectors = diagnostics.filter((diag) => diag.enabled && diag.status === 'healthy').length;
  const automationCoverage = totalConnectors > 0 ? healthyConnectors / totalConnectors : 0;
  const automationProgressPercent = Number.isFinite(automationCoverage) ? Math.round(automationCoverage * 100) : 0;
  const automationProgressValue = Math.min(Math.max(automationProgressPercent, 0), 100);
  const guardrailStatus: GuardrailStatus = (() => {
    if (!heuristicsActive) {
      return 'paused';
    }
    if (
      diagnostics.length === 0 ||
      automationCoverage < 0.5 ||
      (averageDlqRate !== null && averageDlqRate > 5) ||
      mismatchShare > 0.05
    ) {
      return 'degraded';
    }
    return 'clear';
  })();
  const guardrailBadgeLabel = t(`prototype.hero.guardrailBadges.${guardrailStatus}` as const, {
    defaultValue:
      guardrailStatus === 'paused'
        ? 'Automation paused'
        : guardrailStatus === 'degraded'
          ? 'Investigate guardrails'
          : 'All systems go',
  });
  const guardrailDescription = t('prototype.hero.guardrailDescription', {
    defaultValue: '{{healthy}} of {{total}} connectors healthy',
    healthy: formatNumber(healthyConnectors),
    total: formatNumber(totalConnectors),
  });
  const heuristicsMutationPending = updateHeuristicsConfigMutation.isPending;
  const heuristicsBusy = heuristicsLoading || heuristicsRefreshing;
  const optimizationMetrics = optimizationAnalyticsData?.metrics ?? null;
  const optimizationRuns = optimizationAnalyticsData?.recentRuns ?? [];
  const optimizationBusy = optimizationLoading || optimizationRefreshing;
  const heuristicsSectionBusy = heuristicsBusy || optimizationBusy;
  const optimizationLastUpdatedAt = optimizationMetrics?.lastUpdatedAt ?? null;
  const offerStateCounts = optimizationMetrics?.offerStates ?? { enabled: 0, disabled: 0, snoozed: 0 };
  const heuristicResultLabels: Record<'applied' | 'skipped' | 'blocked' | 'error', string> = {
    applied: t('prototype.optimization.results.applied', { defaultValue: 'Applied' }),
    skipped: t('prototype.optimization.results.skipped', { defaultValue: 'Skipped' }),
    blocked: t('prototype.optimization.results.blocked', { defaultValue: 'Blocked' }),
    error: t('prototype.optimization.results.error', { defaultValue: 'Error' }),
  };
  const incrementalRevenueLiftPercent =
    optimizationMetrics && optimizationMetrics.incrementalRoas > 0
      ? Math.max(0, optimizationMetrics.incrementalRoas - 1)
      : null;
  const incrementalRevenueDisplay =
    incrementalRevenueLiftPercent !== null
      ? `${incrementalRevenueLiftPercent >= 0 ? "+" : ""}${formatPercent(incrementalRevenueLiftPercent, {
          maximumFractionDigits: 0,
        })}`
      : "--";
  const incrementalRevenueCaption =
    incrementalRevenueLiftPercent !== null
      ? t("prototype.autopilot.experimentCaption", { defaultValue: "Incremental revenue vs holdout" })
      : t("prototype.autopilot.experimentCaptionPending", { defaultValue: "Awaiting holdout data" });
  const captureStatus: PipelineStatus =
    segmentationRun?.status === 'failed'
      ? 'attention'
      : segmentationRun?.status === 'running' || segmentationRun?.status === 'queued'
        ? 'monitoring'
        : totalProfilesScored > 0
          ? 'healthy'
          : 'monitoring';
  const captureMetric =
    totalProfilesScored > 0
      ? t('prototype.pipeline.capture.metric', {
          defaultValue: '{{count}} profiles scored',
          count: formatNumber(totalProfilesScored),
        })
      : segmentationRunTimestamp
        ? t('prototype.pipeline.capture.metricPending', {
            defaultValue: 'Last run {{timeAgo}}',
            timeAgo: formatRelativeTime(segmentationRunTimestamp),
          })
        : t('prototype.pipeline.capture.metricFallback', { defaultValue: 'Awaiting first segmentation run' });
  const normalizeStatus: PipelineStatus =
    mismatchShare >= 0.05
      ? 'attention'
      : suppressedShare >= 0.02
        ? 'monitoring'
        : hasSegmentationData
          ? 'healthy'
          : 'monitoring';
  const normalizeMetric = hasSegmentationData
    ? t('prototype.pipeline.normalize.metric', {
        defaultValue: '{{percent}} mismatch suppressions',
        percent: formatPercent(mismatchShare, { maximumFractionDigits: 1 }),
      })
    : t('prototype.pipeline.normalize.metricFallback', { defaultValue: 'No suppression audits yet' });
  const pipelineQueueStatus: PipelineStatus =
    averageDlqRate === null
      ? 'monitoring'
      : averageDlqRate <= 2
        ? 'healthy'
        : averageDlqRate <= 5
          ? 'monitoring'
          : 'attention';
  const pipelineQueueMetric =
    averageDlqRate !== null
      ? t('prototype.pipeline.queue.metric', {
          defaultValue: 'DLQ {{rate}}',
          rate: formatPercent(averageDlqRate / 100, { maximumFractionDigits: 1 }),
        })
      : t('prototype.pipeline.queue.metricFallback', { defaultValue: 'Awaiting queue telemetry' });
  const pipelineUploadStatus: PipelineStatus =
    uploadSuccessRate === null
      ? 'monitoring'
      : uploadSuccessRate >= 0.95
        ? 'healthy'
        : uploadSuccessRate >= 0.85
          ? 'monitoring'
          : 'attention';
  const pipelineUploadMetric =
    uploadSuccessRate !== null
      ? t('prototype.pipeline.upload.metric', {
          defaultValue: 'Success {{rate}}',
          rate: formatPercent(uploadSuccessRate, { maximumFractionDigits: 1 }),
        })
      : t('prototype.pipeline.upload.metricFallback', { defaultValue: 'No uploads processed' });
  const pipelineOptimizeStatus: PipelineStatus = heuristicsActive
    ? optimizationMetrics && optimizationMetrics.incrementalRoas >= 4
      ? 'healthy'
      : 'monitoring'
    : 'attention';
  const pipelineOptimizeMetric = !heuristicsActive
    ? t('prototype.pipeline.optimize.metricPaused', { defaultValue: 'Automation paused' })
    : optimizationMetrics
      ? t('prototype.pipeline.optimize.metric', {
          defaultValue: 'ROAS {{value}}×',
          value: formatNumber(optimizationMetrics.incrementalRoas, { maximumFractionDigits: 1 }),
        })
      : t('prototype.pipeline.optimize.metricFallback', { defaultValue: 'Awaiting optimization data' });
  const modeComingSoonLabel = t('prototype.autopilot.modes.comingSoon', { defaultValue: 'Coming soon' });
  const {
    data: offerIntelligence,
    isLoading: offersLoading,
    isFetching: offersRefreshing,
    refetch: refetchOffers,
  } = useAutopilotOfferIntelligence();
  const offerSummaries = useMemo(() => offerIntelligence?.offers ?? [], [offerIntelligence]);
  const topOffers = useMemo(() => offerSummaries.slice(0, 5), [offerSummaries]);
  const offersBusy = offersLoading || offersRefreshing;
  const offersEmpty = !offersBusy && offerSummaries.length === 0;
  const offerWeather = offerIntelligence?.weather ?? {
    latestSeverity: null,
    temperatureCelsius: null,
    precipitationProbability: null,
    observedAt: null,
  };
  const offerStaffing = offerIntelligence?.staffing ?? {
    coverageRatio: null,
    overtimeHours: null,
    callouts: null,
    observedAt: null,
  };
  const offerWeatherLabel = offerWeather.latestSeverity
    ? offerWeather.latestSeverity.replace(/_/g, ' ')
    : t('prototype.offers.summary.weatherUnknown', { defaultValue: 'No signal' });
  const offerWeatherObserved = offerWeather.observedAt
    ? formatRelativeTime(offerWeather.observedAt)
    : t('prototype.offers.summary.observedUnknown', { defaultValue: 'Not updated' });
  const formattedStaffingCoverage =
    offerStaffing.coverageRatio !== null && offerStaffing.coverageRatio !== undefined
      ? formatPercent(offerStaffing.coverageRatio, { maximumFractionDigits: 0 })
      : t('prototype.offers.summary.staffingUnknown', { defaultValue: 'No signal' });
  const staffingObserved = offerStaffing.observedAt
    ? formatRelativeTime(offerStaffing.observedAt)
    : t('prototype.offers.summary.observedUnknown', { defaultValue: 'Not updated' });
  const activeOfferCount = offerSummaries.filter((offer) => offer.state === 'enabled').length;
  const handleBudgetAction = (
    recommendationId: string,
    status: 'applied' | 'dismissed' | 'snoozed' | 'scheduled',
    options?: {
      note?: string | null;
      scheduledFor?: Date | null;
      targetBudgetCents?: number | null;
    },
  ) => {
    updateBudgetRecommendationMutation.mutate(
      {
        recommendationId,
        status,
        note: options?.note ?? null,
        scheduledFor: options?.scheduledFor ? options.scheduledFor.toISOString() : null,
        targetBudgetCents: options?.targetBudgetCents ?? null,
      },
      {
        onSuccess: (result) => {
          const recommendation = result.recommendation;
          toast({
            title: t(`prototype.budgets.toasts.${status}.title`, {
              defaultValue:
                status === 'applied'
                  ? 'Recommendation applied'
                  : status === 'dismissed'
                    ? 'Recommendation dismissed'
                    : status === 'snoozed'
                      ? 'Recommendation snoozed'
                      : 'Recommendation scheduled',
            }),
            description: t(`prototype.budgets.toasts.${status}.description`, {
              defaultValue:
                status === 'applied'
                  ? 'Budget applied successfully.'
                  : status === 'dismissed'
                    ? 'Recommendation dismissed.'
                    : status === 'snoozed'
                      ? 'Recommendation snoozed.'
                      : 'Recommendation scheduled.',
              campaign: (recommendation.metadata as { campaign?: string } | undefined)?.campaign ?? recommendation.recommendationType,
            }),
          });
          void refetchBudgets();
        },
        onError: (error: unknown) => {
          toast({
            title: t('prototype.budgets.toasts.error.title', { defaultValue: 'Unable to update recommendation' }),
            description: error instanceof Error ? error.message : String(error),
            variant: 'destructive',
          });
        },
      },
    );
  };
  const handleHeuristicsToggle = (checked: boolean) => {
    updateHeuristicsConfigMutation.mutate(
      {
        killSwitchEnabled: !checked,
      },
      {
        onSuccess: () => {
          toast({
            title: checked
              ? t('prototype.optimization.toasts.resume.title', { defaultValue: 'Optimization resumed' })
              : t('prototype.optimization.toasts.pause.title', { defaultValue: 'Optimization paused' }),
            description: checked
              ? t('prototype.optimization.toasts.resume.description', {
                  defaultValue: 'Autopilot heuristics are active for this tenant.',
                })
              : t('prototype.optimization.toasts.pause.description', {
                  defaultValue: 'Autopilot heuristics are paused until you resume.',
                }),
          });
          void refetchHeuristics();
          void refetchOptimization();
        },
        onError: () => {
          toast({
            title: t('prototype.optimization.toasts.error.title', { defaultValue: 'Unable to update heuristics' }),
            description: t('prototype.optimization.toasts.error.description', {
              defaultValue: 'We could not update the optimization preferences. Try again.',
            }),
            variant: 'destructive',
          });
        },
      },
    );
  };
  const handleExperimentVariantChange = (
    experimentKey: string,
    variant: string,
    options?: { notes?: string | null },
  ) => {
    updateExperimentMutation.mutate(
      {
        experimentKey,
        variant,
        notes: options?.notes ?? null,
      },
      {
        onSuccess: (state) => {
          toast({
            title: t('prototype.experiments.toasts.update.title', { defaultValue: 'Experiment updated' }),
            description: t('prototype.experiments.toasts.update.description', {
              defaultValue: 'Variant set to {{variant}}.',
              variant: state.assignment.variant,
            }),
          });
          void refetchExperiments();
        },
        onError: (error: unknown) => {
          toast({
            title: t('prototype.experiments.toasts.error.title', { defaultValue: 'Unable to update experiment' }),
            description: error instanceof Error ? error.message : String(error),
            variant: 'destructive',
          });
        },
      },
    );
  };
  const experimentMutationPending = updateExperimentMutation.isPending;
  const handleRollupDownload = () => {
    downloadRollupsMutation.mutate(
      { startDate: defaultStartDate, endDate: defaultEndDate },
      {
        onSuccess: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'autopilot-daily-rollups.csv';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast({
            title: t('prototype.rollups.toasts.download.title', { defaultValue: 'Export ready' }),
            description: t('prototype.rollups.toasts.download.description', {
              defaultValue: 'CSV exported for {{range}}.',
              range: `${defaultStartDate} → ${defaultEndDate}`,
            }),
          });
          void refetchRollups();
        },
        onError: (error: unknown) => {
          toast({
            title: t('prototype.rollups.toasts.error.title', { defaultValue: 'Unable to export rollups' }),
            description: error instanceof Error ? error.message : String(error),
            variant: 'destructive',
          });
        },
      },
    );
  };
  const franchiseMutationPending = downloadFranchiseMutation.isPending;
  const franchiseRangeLabel = franchiseWindow
    ? `${franchiseWindow.startDate.slice(0, 10)} → ${franchiseWindow.endDate.slice(0, 10)}`
    : `${franchiseStartDate} → ${franchiseEndDate}`;
  const handleFranchiseDownload = () => {
    downloadFranchiseMutation.mutate(
      { startDate: franchiseStartDate, endDate: franchiseEndDate },
      {
        onSuccess: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'autopilot-franchise-analytics.csv';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast({
            title: t('prototype.franchise.toasts.download.title', { defaultValue: 'Export ready' }),
            description: t('prototype.franchise.toasts.download.description', {
              defaultValue: 'CSV exported for {{range}}.',
              range: franchiseRangeLabel,
            }),
          });
          void refetchFranchise();
        },
        onError: (error: unknown) => {
          toast({
            title: t('prototype.franchise.toasts.error.title', { defaultValue: 'Unable to export franchise analytics' }),
            description:
              error instanceof Error
                ? error.message
                : t('prototype.franchise.toasts.error.description', {
                    defaultValue: 'Please try again in a moment.',
                  }),
            variant: 'destructive',
          });
        },
      },
    );
  };
  const requireTenantSlug = () => {
    if (!tenantSlug) {
      throw new Error(
        t("prototype.errors.noTenant", {
          defaultValue: "Select a tenant before running Autopilot onboarding.",
        }),
      );
    }
    return tenantSlug;
  };

  const buildSampleRows = (prefix: string) => [
    {
      externalId: `${prefix}-sandbox-${Date.now()}-001`,
      valueCents: 3425,
      hashedEmail: `${prefix}-sample-email-1`,
      hashedPhone: `${prefix}-sample-phone-1`,
    },
    {
      externalId: `${prefix}-sandbox-${Date.now()}-002`,
      valueCents: 2890,
      hashedEmail: `${prefix}-sample-email-2`,
      hashedPhone: `${prefix}-sample-phone-2`,
    },
    {
      externalId: `${prefix}-sandbox-${Date.now()}-003`,
      valueCents: 4180,
      hashedEmail: `${prefix}-sample-email-3`,
      hashedPhone: `${prefix}-sample-phone-3`,
    },
  ];

  const handleSegmentationRun = (mode: "async" | "sync" = "sync") => {
    try {
      requireTenantSlug();
    } catch {
      toast({
        title: t("prototype.errors.noTenant", {
          defaultValue: "Select a tenant before running Autopilot onboarding.",
        }),
        variant: "destructive",
      });
      return;
    }
    triggerSegmentationMutation.mutate(
      { mode },
      {
        onSuccess: (result) => {
          if ("run" in result) {
            toast({
              title: t("prototype.segmentation.toasts.runSuccess", { defaultValue: "Segmentation updated" }),
              description: t("prototype.segmentation.toasts.runSuccessDetail", {
                defaultValue: "{{count}} profiles scored in the latest pass.",
                count: formatNumber(result.run.segmentCount ?? 0),
              }),
            });
          } else {
            toast({
              title: t("prototype.segmentation.toasts.jobQueued", { defaultValue: "Segmentation queued" }),
              description: t("prototype.segmentation.toasts.jobQueuedDetail", {
                defaultValue: "We'll notify you when the new segments are ready.",
              }),
            });
          }
          void refetchSegmentation();
        },
        onError: (error: unknown) => {
          toast({
            title: t("prototype.segmentation.toasts.runError", { defaultValue: "Unable to refresh segmentation" }),
            description: error instanceof Error ? error.message : String(error),
            variant: "destructive",
          });
        },
      },
    );
  };

  const toggleConnectorMutation = useMutation<
    AutopilotConnectorAccount,
    Error,
    { connector: ConnectorType; environment: "sandbox" | "production"; enabled: boolean }
  >({
    mutationFn: async ({ connector, environment, enabled }) => {
      const slug = requireTenantSlug();
      return setAutopilotConnectorAccountEnabled({
        tenantSlug: slug,
        connector,
        environment,
        enabled,
      });
    },
    onSuccess: (account) => {
      toast({
        title: account.enabled
          ? t("prototype.connectors.toasts.enableSuccess", { defaultValue: "Connector enabled" })
          : t("prototype.connectors.toasts.disableSuccess", { defaultValue: "Connector disabled" }),
      });
      void queryClient.invalidateQueries({ queryKey: ["autopilot"] });
      void refetchDiagnostics();
    },
    onError: (error: unknown) => {
      toast({
        title: t("prototype.connectors.toasts.actionError", { defaultValue: "Unable to complete connector action" }),
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    },
  });

  const replayUploadMutation = useMutation<AutopilotConnectorUpload, Error, { uploadId: string }>({
    mutationFn: async ({ uploadId }) => {
      const slug = requireTenantSlug();
      return replayAutopilotConnectorUpload({
        tenantSlug: slug,
        uploadId,
      });
    },
    onSuccess: () => {
      toast({
        title: t("prototype.connectors.toasts.replaySuccess", { defaultValue: "Replay queued" }),
      });
      void queryClient.invalidateQueries({ queryKey: ["autopilot"] });
      void refetchDiagnostics();
    },
    onError: (error: unknown) => {
      toast({
        title: t("prototype.connectors.toasts.actionError", { defaultValue: "Unable to complete connector action" }),
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    },
  });

const handleToggleConnector = (
    connector: ConnectorType,
    environment: "sandbox" | "production",
    enabled: boolean,
  ) => {
    toggleConnectorMutation.mutate({ connector, environment, enabled });
  };

  const handleReplayUpload = (uploadId: string) => {
    replayUploadMutation.mutate({ uploadId });
  };

  const runCanaryMutation = useMutation({
    mutationFn: async () => {
      const slug = requireTenantSlug();
      await Promise.all([
        enqueueAutopilotConnectorUpload({
          tenantSlug: slug,
          connector: "google_ads",
          environment: "sandbox",
          rows: buildSampleRows("google"),
          isCanary: true,
        }),
        enqueueAutopilotConnectorUpload({
          tenantSlug: slug,
          connector: "meta_ads",
          environment: "sandbox",
          rows: buildSampleRows("meta"),
          isCanary: true,
        }),
      ]);
    },
    onSuccess: () => {
      toast({
        title: t("prototype.onboarding.canary.success", { defaultValue: "Canary upload queued" }),
        description: t("prototype.onboarding.canary.description", {
          defaultValue: "Check diagnostics for the latest sandbox batches.",
        }),
      });
      void queryClient.invalidateQueries({ queryKey: ["autopilot"] });
      void refetchDiagnostics();
    },
    onError: (error: unknown) => {
      toast({
        title: t("prototype.onboarding.canary.error", { defaultValue: "Canary upload failed" }),
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    },
  });

  const metricCards = useMemo(() => {
    const statusLabel = (status: PipelineStatus | undefined) =>
      status
        ? t(`prototype.connectors.status.${status}` as const, {
            defaultValue:
              status === "healthy"
                ? t("prototype.badges.healthy", { defaultValue: "Healthy" })
                : status === "monitoring"
                  ? t("prototype.badges.monitoring", { defaultValue: "Monitoring" })
                  : t("prototype.badges.attention", { defaultValue: "Needs attention" }),
          })
        : t("prototype.badges.monitoring", { defaultValue: "Monitoring" });

    const diagnosticsMap = new Map<ConnectorType, AutopilotConnectorDiagnostic>();
    diagnostics.forEach((diag) => {
      diagnosticsMap.set(diag.connector as ConnectorType, diag);
    });

    const connectorCards = CONNECTOR_TYPES.map((connector) => {
      const diag = diagnosticsMap.get(connector);
      const copy = CONNECTOR_METRIC_COPY[connector];
      const matchRate = typeof diag?.matchRate === "number" ? diag.matchRate / 100 : undefined;

      return {
        label: t(copy.labelKey, { defaultValue: copy.defaultLabel }),
        value: matchRate !== undefined ? formatPercent(matchRate, { maximumFractionDigits: 1 }) : "--",
        trend:
          diag?.lastSuccessAt != null
            ? t(copy.trendKey, {
                defaultValue: copy.defaultTrend,
                timeAgo: formatRelativeTime(diag.lastSuccessAt),
              })
            : t(copy.trendFallbackKey, { defaultValue: copy.defaultFallback }),
        badge: statusLabel(diag?.status),
        icon: copy.icon,
      };
    });

    const queueStatus =
      averageDlqRate !== null && averageDlqRate <= 1
        ? "healthy"
        : averageDlqRate !== null && averageDlqRate <= 5
          ? "monitoring"
          : "attention";

    const failures = diagnostics.reduce(
      (acc, diag) => acc + diag.recentUploads.filter((upload) => upload.dlq).length,
      0,
    );
    const uploadsCount = diagnostics.reduce((acc, diag) => acc + diag.recentUploads.length, 0);

    const queueCard = {
      label: t("prototype.metrics.dlqRate.label", {
        defaultValue: "Queue DLQ rate",
      }),
      value:
        averageDlqRate !== null && averageDlqRate !== undefined
          ? formatPercent(averageDlqRate / 100, { maximumFractionDigits: 1 })
          : "--",
      trend:
        diagnostics.length > 0
          ? t("prototype.metrics.dlqRate.trend", {
              defaultValue: "{{failures}} of {{uploads}} uploads in DLQ",
              failures: formatNumber(failures),
              uploads: formatNumber(uploadsCount),
            })
          : t("prototype.metrics.dlqRate.trendFallback", { defaultValue: "Awaiting first sandbox uploads" }),
      badge: statusLabel(queueStatus),
      icon: Activity,
    };

    return [...connectorCards, queueCard];
  }, [averageDlqRate, diagnostics, formatNumber, formatPercent, formatRelativeTime, t]);
  const pipelineStages = useMemo(
    () => [
      {
        key: "capture" as const,
        title: t("prototype.pipeline.capture.title", {
          defaultValue: "Capture",
        }),
        description: t("prototype.pipeline.capture.description", {
          defaultValue: "POS, kiosk, and QR prompts collect consented identifiers with receipt metadata.",
        }),
        metric: captureMetric,
        status: captureStatus,
      },
      {
        key: "normalize" as const,
        title: t("prototype.pipeline.normalize.title", {
          defaultValue: "Normalize",
        }),
        description: t("prototype.pipeline.normalize.description", {
          defaultValue:
            "Receipts land in marketing_events with hashed emails/phones, location, and gclid/gbraid context.",
        }),
        metric: normalizeMetric,
        status: normalizeStatus,
      },
      {
        key: "queue" as const,
        title: t("prototype.pipeline.queue.title", {
          defaultValue: "Queue",
        }),
        description: t("prototype.pipeline.queue.description", {
          defaultValue: "Hourly uploader batches 1,000-row payloads with exponential backoff and replay guards.",
        }),
        metric: pipelineQueueMetric,
        status: pipelineQueueStatus,
      },
      {
        key: "upload" as const,
        title: t("prototype.pipeline.upload.title", {
          defaultValue: "Upload",
        }),
        description: t("prototype.pipeline.upload.description", {
          defaultValue: "Google Enhanced Conversions + Meta CAPI offline share revenue, identifiers, and click IDs.",
        }),
        metric: pipelineUploadMetric,
        status: pipelineUploadStatus,
      },
      {
        key: "optimize" as const,
        title: t("prototype.pipeline.optimize.title", {
          defaultValue: "Optimize",
        }),
        description: t("prototype.pipeline.optimize.description", {
          defaultValue: "Autopilot reallocates budget, geo radius, and playbooks when incremental lift clears guardrails.",
        }),
        metric: pipelineOptimizeMetric,
        status: pipelineOptimizeStatus,
      },
    ],
    [
      captureMetric,
      captureStatus,
      normalizeMetric,
      normalizeStatus,
      pipelineQueueMetric,
      pipelineQueueStatus,
      pipelineUploadMetric,
      pipelineUploadStatus,
      pipelineOptimizeMetric,
      pipelineOptimizeStatus,
      t,
    ],
  );


const connectorTabs = useMemo<ConnectorTabConfig[]>(() => {
  const diagnosticsMap: Partial<Record<ConnectorType, AutopilotConnectorDiagnostic>> = {};
  diagnostics.forEach((diag) => {
    diagnosticsMap[diag.connector as ConnectorType] = diag;
  });

  const connectorLabel = (connector: ConnectorType) => {
    const keyMap: Record<ConnectorType, string> = {
      google_ads: "prototype.connectors.google.label",
      meta_ads: "prototype.connectors.meta.label",
      waze_ads: "prototype.connectors.waze.label",
      nextdoor_ads: "prototype.connectors.nextdoor.label",
      sms_campaign: "prototype.connectors.sms.label",
      email_campaign: "prototype.connectors.email.label",
    };
    const defaultLabels: Record<ConnectorType, string> = {
      google_ads: "Google Ads",
      meta_ads: "Meta Ads",
      waze_ads: "Waze Ads",
      nextdoor_ads: "Nextdoor Ads",
      sms_campaign: "SMS Campaigns",
      email_campaign: "Email Campaigns",
    };
    return t(keyMap[connector], { defaultValue: defaultLabels[connector] });
  };

  const uploadsByConnector: Record<ConnectorType, AutopilotConnectorUpload[]> = {
    google_ads: googleUploads.length > 0 ? googleUploads : diagnosticsMap.google_ads?.recentUploads ?? [],
    meta_ads: metaUploads.length > 0 ? metaUploads : diagnosticsMap.meta_ads?.recentUploads ?? [],
    waze_ads: wazeUploads.length > 0 ? wazeUploads : diagnosticsMap.waze_ads?.recentUploads ?? [],
    nextdoor_ads: nextdoorUploads.length > 0 ? nextdoorUploads : diagnosticsMap.nextdoor_ads?.recentUploads ?? [],
    sms_campaign: smsUploads.length > 0 ? smsUploads : diagnosticsMap.sms_campaign?.recentUploads ?? [],
    email_campaign: emailUploads.length > 0 ? emailUploads : diagnosticsMap.email_campaign?.recentUploads ?? [],
  };

  const loadingByConnector: Record<ConnectorType, boolean> = {
    google_ads: googleUploadsLoading,
    meta_ads: metaUploadsLoading,
    waze_ads: wazeUploadsLoading,
    nextdoor_ads: nextdoorUploadsLoading,
    sms_campaign: smsUploadsLoading,
    email_campaign: emailUploadsLoading,
  };

  const refreshingByConnector: Record<ConnectorType, boolean> = {
    google_ads: googleUploadsRefreshing,
    meta_ads: metaUploadsRefreshing,
    waze_ads: wazeUploadsRefreshing,
    nextdoor_ads: nextdoorUploadsRefreshing,
    sms_campaign: smsUploadsRefreshing,
    email_campaign: emailUploadsRefreshing,
  };

  const mapUploadStatus = (status: string, dlq: boolean): PipelineStatus => {
    if (dlq || status === "failed") {
      return "attention";
    }
    if (status === "pending" || status === "in_flight") {
      return "monitoring";
    }
    return "healthy";
  };

  const uploadStatusLabel = (status: string) =>
    t(`prototype.connectors.table.statuses.${status}` as const, {
      defaultValue:
        status === "succeeded"
          ? t("prototype.rows.status.completed", { defaultValue: "Completed" })
          : status === "canary"
            ? t("prototype.rows.status.canary", { defaultValue: "Canary" })
            : status === "in_flight"
              ? t("prototype.rows.status.inflight", { defaultValue: "In flight" })
              : status === "failed"
                ? t("prototype.rows.status.retry", { defaultValue: "Retry" })
                : t("prototype.rows.status.pending", { defaultValue: "Pending" }),
    });

  const formatHealthSummary = (matchRate: number, dlqRate: number) =>
    t("prototype.connectors.healthSummary", {
      defaultValue: "{{matchRate}} match - {{dlqRate}} DLQ",
      matchRate: formatPercent(matchRate / 100, { maximumFractionDigits: 1 }),
      dlqRate: formatPercent(dlqRate / 100, { maximumFractionDigits: 1 }),
    });

  const mapUploadRow = (upload: AutopilotConnectorUpload, includeConnector: boolean): ConnectorTableRow => {
    const successSummary = t("prototype.connectors.table.rowsSummary", {
      defaultValue: "{{success}} of {{total}} rows accepted",
      success: formatNumber(upload.successCount),
      total: formatNumber(upload.totalRows),
    });
    const matchLabel =
      upload.matchRate !== undefined
        ? formatPercent(upload.matchRate / 100, { maximumFractionDigits: 1 })
        : "--";
    const revenueCents = upload.rowStatuses.reduce((sum, row) => sum + (row.valueCents ?? 0), 0);
    const revenue = revenueCents > 0 ? formatCurrency(revenueCents, "USD", { isMinorUnit: true }) : "--";
    const rejectionSample = upload.rowStatuses.find((row) => row.status === "rejected");
    let note: string;
    if (upload.dlq) {
      note = t("prototype.connectors.table.notes.dlq", {
        defaultValue: "In DLQ awaiting review",
      });
    } else if (upload.lastError) {
      note = upload.lastError;
    } else if (rejectionSample) {
      note = t("prototype.connectors.table.notes.rejection", {
        defaultValue: "Rejected row: {{reason}}",
        reason:
          rejectionSample.rejectionReason ??
          t("prototype.connectors.table.notes.lowConfidence", { defaultValue: "Low confidence" }),
      });
    } else {
      note = t("prototype.connectors.table.notes.accepted", { defaultValue: "All rows accepted" });
    }
    if (upload.isCanary) {
      const canaryLabel = t("prototype.connectors.table.notes.canary", { defaultValue: "Canary validation" });
      note = note ? `${canaryLabel} - ${note}` : canaryLabel;
    }
    const timestamp = upload.completedAt ?? upload.updatedAt ?? upload.startedAt ?? upload.createdAt ?? null;
    return {
      id: upload.id,
      batchId: upload.batchId,
      connectorLabel: includeConnector ? connectorLabel(upload.connector as ConnectorType) : undefined,
      status: mapUploadStatus(upload.status, upload.dlq),
      statusText: uploadStatusLabel(upload.status),
      conversions: successSummary,
      matchRate: matchLabel,
      revenue,
      note,
      relativeTime: timestamp ? formatRelativeTime(timestamp) : "--",
      timestamp,
    };
  };

  const mapDlqRow = (upload: AutopilotConnectorUpload): ConnectorDlqRow => {
    const timestamp = upload.updatedAt ?? upload.startedAt ?? upload.createdAt ?? null;
    const rejectionSample = upload.rowStatuses.find((row) => row.status === "rejected");
    const reason =
      upload.lastError ??
      rejectionSample?.rejectionReason ??
      t("prototype.connectors.dlq.reasonFallback", { defaultValue: "Review platform response" });
    const rowsSummary = t("prototype.connectors.dlq.rowsSummary", {
      defaultValue: "{{failures}} of {{total}} rows need attention",
      failures: formatNumber(upload.failureCount),
      total: formatNumber(upload.totalRows),
    });
    return {
      id: upload.id,
      batchId: upload.batchId,
      rowsSummary,
      reason,
      relativeTime: timestamp ? formatRelativeTime(timestamp) : "--",
      timestamp,
      replayAttempt: upload.replayAttempt,
    };
  };

  const tabs: ConnectorTabConfig[] = [];

  CONNECTOR_TYPES.forEach((connector) => {
    const diag = diagnosticsMap[connector];
    const uploadsSource = uploadsByConnector[connector];
    const dlqRows = (dlqByConnector.get(`${connector}:sandbox`) ?? []).map(mapDlqRow);

    tabs.push({
      value: connector,
      label: connectorLabel(connector),
      health: diag
        ? formatHealthSummary(diag.matchRate, diag.dlqRate)
        : t("prototype.connectors.healthFallback", { defaultValue: "Awaiting telemetry" }),
      status: diag?.status ?? "monitoring",
      uploads: uploadsSource.map((upload) => mapUploadRow(upload, false)),
      dlqUploads: dlqRows,
      isConnector: true,
      enabled: diag?.enabled ?? false,
      credentialStatus: diag?.credentialStatus ?? "none",
      lastSuccessAt: diag?.lastSuccessAt ?? null,
      lastFailureAt: diag?.lastFailureAt ?? null,
      hasSandboxData: diag?.hasSandboxData ?? uploadsSource.some((upload) => upload.environment === "sandbox"),
      totalUploads: diag?.totalUploads ?? uploadsSource.length,
      uploadsLoading: loadingByConnector[connector],
      uploadsRefreshing: refreshingByConnector[connector],
    });
  });

  const queueSource =
    queueUploads.length > 0
      ? queueUploads
      : CONNECTOR_TYPES.flatMap((connector) => uploadsByConnector[connector] ?? []);
  const queueRows = queueSource
    .map((upload) => mapUploadRow(upload, true))
    .sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return bTime - aTime;
    });

  const queueStatus =
    averageDlqRate !== null && averageDlqRate <= 1
      ? "healthy"
      : averageDlqRate !== null && averageDlqRate <= 5
        ? "monitoring"
        : "attention";

  tabs.push({
    value: "queue",
    label: t("prototype.connectors.queue.label", { defaultValue: "Upload queue" }),
    health:
      averageDlqRate !== null
        ? formatHealthSummary(100 - averageDlqRate, averageDlqRate)
        : t("prototype.connectors.queue.health", { defaultValue: "Monitoring recent uploads" }),
    status: queueStatus,
    uploads: queueRows,
    isConnector: false,
    uploadsLoading: queueUploadsLoading,
    uploadsRefreshing: queueUploadsRefreshing,
  });

  return tabs;
}, [
  averageDlqRate,
  diagnostics,
  dlqByConnector,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatRelativeTime,
  googleUploads,
  googleUploadsLoading,
  googleUploadsRefreshing,
  metaUploads,
  metaUploadsLoading,
  metaUploadsRefreshing,
  wazeUploads,
  wazeUploadsLoading,
  wazeUploadsRefreshing,
  nextdoorUploads,
  nextdoorUploadsLoading,
  nextdoorUploadsRefreshing,
  smsUploads,
  smsUploadsLoading,
  smsUploadsRefreshing,
  emailUploads,
  emailUploadsLoading,
  emailUploadsRefreshing,
  queueUploads,
  queueUploadsLoading,
  queueUploadsRefreshing,
  t,
]);

  const trackEducationClick = useCallback((entryId: string, persona: EducationPersona, connector?: ConnectorType | null) => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      const analyticsWindow = window as typeof window & {
        analytics?: { track?: (event: string, payload?: Record<string, unknown>) => void };
      };
      analyticsWindow.analytics?.track?.("Autopilot Education Click", {
        entryId,
        persona,
        connector: connector ?? null,
      });
    } catch (error) {
      console.warn("[autopilot] failed to emit education analytics event", error);
    }

    try {
      const payload = JSON.stringify({
        entryId,
        persona,
        connector: connector ?? null,
        sentAt: new Date().toISOString(),
      });
      if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon("/api/analytics/autopilot-education", blob);
      }
    } catch (error) {
      console.warn("[autopilot] failed to enqueue education analytics payload", error);
    }
  }, []);

  const educationEntries = useMemo(() => {
    const entries: EducationEntry[] = [];
    const personaLabel = (persona: EducationPersona) =>
      t(`prototype.education.persona.${persona}`, {
        defaultValue: persona === "operator" ? "Operators" : "Account managers",
      });

    const seen = new Set<string>();
    diagnostics.forEach((diag) => {
      const totalUploads = diag.totalUploads ?? 0;
      const matchRate = diag.matchRate ?? 0;
      const dlqRate = diag.dlqRate ?? 0;
      const needsActivation = totalUploads === 0 || !diag.hasSandboxData || !diag.enabled;
      const needsOptimization = !needsActivation && (matchRate <= 60 || dlqRate >= 10);

      const variant = needsActivation ? "activate" : needsOptimization ? "optimize" : null;
      if (!variant) {
        return;
      }

      const keyBase = `prototype.education.cards.${diag.connector}.${variant}`;
      const title = t(`${keyBase}.title`, { defaultValue: "" });
      if (!title) {
        return;
      }

      const description = t(`${keyBase}.description`, { defaultValue: "" });
      const ctaLabel = t(`${keyBase}.cta`, { defaultValue: "" });
      const entryId = `${diag.connector}.${variant}`;
      if (seen.has(entryId)) {
        return;
      }
      seen.add(entryId);

      entries.push({
        id: entryId,
        persona: "operator",
        connector: diag.connector,
        severity: needsActivation || dlqRate >= 10 ? "warning" : "info",
        title,
        description,
        audience: personaLabel("operator"),
        ctaLabel: ctaLabel || undefined,
        href: EDUCATION_DOC_LINKS[entryId] ?? EDUCATION_DOC_LINKS.general,
        icon: CONNECTOR_METRIC_COPY[diag.connector].icon,
      });
    });

    const ensureEntry = (entry: EducationEntry) => {
      if (!seen.has(entry.id)) {
        seen.add(entry.id);
        entries.push(entry);
      }
    };

    const generalOperatorTitle = t("prototype.education.cards.general.operator.title", { defaultValue: "" });
    if (generalOperatorTitle) {
      ensureEntry({
        id: "general.operator",
        persona: "operator",
        connector: null,
        severity: "info",
        title: generalOperatorTitle,
        description: t("prototype.education.cards.general.operator.description", {
          defaultValue: "Review Autopilot playbooks to keep automation healthy.",
        }),
        audience: personaLabel("operator"),
        ctaLabel: t("prototype.education.cards.general.operator.cta", { defaultValue: "" }) || undefined,
        href: EDUCATION_DOC_LINKS.general,
        icon: Lamp,
      });
    }

    const managerTitle = t("prototype.education.cards.general.manager.title", { defaultValue: "" });
    if (managerTitle) {
      ensureEntry({
        id: "general.manager",
        persona: "manager",
        connector: null,
        severity: "info",
        title: managerTitle,
        description: t("prototype.education.cards.general.manager.description", {
          defaultValue: "Share wins with CX/AM teams and review adoption metrics weekly.",
        }),
        audience: personaLabel("manager"),
        ctaLabel: t("prototype.education.cards.general.manager.cta", { defaultValue: "" }) || undefined,
        href: EDUCATION_DOC_LINKS.general,
        icon: BookOpen,
      });
    }

    return entries.slice(0, 4);
  }, [diagnostics, t]);

  const handleEducationClick = useCallback(
    (entry: EducationEntry) => {
      trackEducationClick(entry.id, entry.persona, entry.connector ?? null);
      if (entry.href && typeof window !== "undefined") {
        window.open(entry.href, "_blank", "noopener");
        return;
      }
      toast({
        title: t("prototype.education.toast.title", { defaultValue: "Resource coming soon" }),
        description: t("prototype.education.toast.description", {
          defaultValue: "Our team is finalizing this guide. Reach out to CX for immediate support.",
        }),
      });
    },
    [toast, t, trackEducationClick],
  );

  const pendingToggleConnector = toggleConnectorMutation.isPending ? toggleConnectorMutation.variables?.connector : undefined;
  const uploadsRefreshing =
    diagnosticsRefreshing ||
    googleUploadsRefreshing ||
    metaUploadsRefreshing ||
    wazeUploadsRefreshing ||
    nextdoorUploadsRefreshing ||
    smsUploadsRefreshing ||
    emailUploadsRefreshing ||
    queueUploadsRefreshing;
  const hasConnectorData = connectorTabs.some((tab) => tab.uploads.length > 0 || (tab.dlqUploads?.length ?? 0) > 0);
  const refreshConnectorData = () => {
    void Promise.all([
      refetchDiagnostics(),
      refetchGoogleUploads(),
      refetchMetaUploads(),
      refetchWazeUploads(),
      refetchNextdoorUploads(),
      refetchSmsUploads(),
      refetchEmailUploads(),
      refetchQueueUploads(),
    ]);
  };

  const playbookHighlights = useMemo<PlaybookHighlight[]>(() => {
    if (offerSummaries.length === 0) {
      return [
        {
          id: "pending",
          title: t("prototype.playbooks.empty.title", { defaultValue: "No automation highlights yet" }),
          description: t("prototype.playbooks.empty.description", {
            defaultValue: "Enable offer heuristics to populate highlights.",
          }),
          audience: t("prototype.playbooks.empty.audience", { defaultValue: "Awaiting guardrail telemetry" }),
          badgeLabel: t("prototype.playbooks.status.pending", { defaultValue: "Pending data" }),
          badgeClassName: "bg-slate-500/10 text-slate-600 dark:bg-slate-500/20 dark:text-slate-200",
        },
      ];
    }

    return offerSummaries.slice(0, 3).map((offer) => {
      const stateLabel = t(`prototype.offers.states.${offer.state}` as const, {
        defaultValue:
          offer.state === "enabled" ? "Enabled" : offer.state === "snoozed" ? "Snoozed" : "Disabled",
      });
      const channelLabel = translateConnectorLabel(offer.channel);
      const description = t("prototype.playbooks.dynamic.description", {
        defaultValue: "{{channel}} · {{state}} · Margin {{margin}} at {{price}}",
        channel: channelLabel,
        state: stateLabel,
        margin: formatPercent(offer.marginPercent, { maximumFractionDigits: 0 }),
        price: formatCurrency(offer.priceCents / 100),
      });
      const segments = offer.signals.demandSegments?.slice(0, 3) ?? [];
      const signals: string[] = [];
      if (offer.signals.weatherSeverity) {
        signals.push(
          t("prototype.playbooks.dynamic.signalWeather", {
            defaultValue: "{{severity}} weather",
            severity: offer.signals.weatherSeverity.replace(/_/g, " "),
          }),
        );
      }
      if (offer.signals.staffingCoverageRatio !== null && offer.signals.staffingCoverageRatio !== undefined) {
        signals.push(
          t("prototype.playbooks.dynamic.signalStaffing", {
            defaultValue: "{{coverage}} staffing",
            coverage: formatPercent(offer.signals.staffingCoverageRatio, { maximumFractionDigits: 0 }),
          }),
        );
      }
      const audience =
        segments.length > 0
          ? t("prototype.playbooks.dynamic.audience", {
              defaultValue: "Segments: {{segments}}",
              segments: segments.join(", "),
            })
          : signals.length > 0
            ? signals.join(" • ")
            : t("prototype.playbooks.dynamic.audienceFallback", { defaultValue: "Awaiting guardrail signals" });

      return {
        id: offer.id,
        title: offer.name,
        description,
        audience,
        badgeLabel: stateLabel,
        badgeClassName: offerStateBadgeStyles[offer.state],
      };
    });
  }, [formatCurrency, formatPercent, offerSummaries, t, translateConnectorLabel]);

  return (
    <DashboardLayoutV3>
      <div className="flex flex-col gap-8 pb-16">
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-slate-900 text-white shadow-xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.18),transparent_45%)]" />
          <div className="relative flex flex-col gap-8 px-6 py-8 lg:px-12 lg:py-12">
            <div className="flex flex-wrap items-center gap-3">
              <Badge className="bg-white/15 text-white ring-1 ring-inset ring-white/40 backdrop-blur">
                {t("prototype.hero.badge", { defaultValue: "Phase 0 Prototype" })}
              </Badge>
              <span className="text-sm font-medium text-emerald-50">
                {heroSyncLabel}
              </span>
            </div>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl space-y-4">
                <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
                  {t("prototype.hero.title", {
                    defaultValue: "Closed Loop Ad Intelligence",
                  })}
                </h1>
                <p className="text-base text-emerald-100 sm:text-lg">
                  {t("prototype.hero.subtitle", {
                    defaultValue:
                      "Track receipts to revenue, route conversions to Google & Meta automatically, and let Autopilot reinvest in the dayparts that drive incremental lift.",
                  })}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="default" size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50">
                    <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t("prototype.hero.actions.primary", {
                      defaultValue: "Preview automation playbooks",
                    })}
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-white/40 bg-white/10 text-white hover:bg-white/20"
                  >
                    <ArrowUpRight className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t("prototype.hero.actions.secondary", {
                      defaultValue: "Share demo script",
                    })}
                  </Button>
                </div>
              </div>
              <div className="flex w-full flex-col gap-3 rounded-2xl bg-white/10 p-5 shadow-inner backdrop-blur md:w-auto">
                <div className="flex items-center justify-between text-sm text-emerald-50">
                  <span>{t("prototype.hero.guardrailLabel", { defaultValue: "Guardrail status" })}</span>
                  <Badge className={cn("ring-1 ring-inset ring-white/40 backdrop-blur", guardrailBadgeTone[guardrailStatus])}>
                    {guardrailBadgeLabel}
                  </Badge>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-semibold">
                    {formatNumber(automationProgressValue, { maximumFractionDigits: 0 })}%
                  </span>
                  <span className="text-sm text-emerald-100">
                    {guardrailDescription}
                  </span>
                </div>
                <Progress
                  value={automationProgressValue}
                  aria-label={t("prototype.hero.guardrailProgress", {
                    defaultValue: "Guardrail deployment progress",
                  })}
                  className="h-2 bg-white/20 [&>div]:bg-white"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground">
                {t('prototype.franchise.title', { defaultValue: 'Franchise insights' })}
              </h2>
              <p className="max-w-2xl text-sm text-muted-foreground">
                {t('prototype.franchise.subtitle', {
                  defaultValue: 'Compare Autopilot performance across franchise locations for the last 30 days.',
                })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('prototype.franchise.window', {
                  defaultValue: 'Window: {{range}}',
                  range: franchiseRangeLabel,
                })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchFranchise()}
                disabled={franchiseRefreshing}
                aria-label={t('prototype.franchise.refresh', { defaultValue: 'Refresh franchise analytics' })}
              >
                {franchiseRefreshing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t('prototype.franchise.refresh', { defaultValue: 'Refresh' })}
              </Button>
              <Button
                size="sm"
                onClick={handleFranchiseDownload}
                disabled={franchiseMutationPending}
                aria-label={t('prototype.franchise.download', { defaultValue: 'Download franchise CSV' })}
              >
                {franchiseMutationPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t('prototype.franchise.download', { defaultValue: 'Download CSV' })}
              </Button>
            </div>
          </div>
          {franchiseLoading ? (
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((index) => (
                <Skeleton key={index} className="h-24 rounded-2xl" />
              ))}
            </div>
          ) : franchiseRollups.length === 0 ? (
            <p className="mt-6 text-sm text-muted-foreground">
              {t('prototype.franchise.empty', {
                defaultValue: 'No franchise analytics available for this window.',
              })}
            </p>
          ) : (
            <div className="mt-6 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('prototype.franchise.summary.uploads', { defaultValue: 'Total uploads' })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">{formatNumber(franchiseSummary.totalUploads)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('prototype.franchise.summary.dlqRate', { defaultValue: 'DLQ rate' })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">
                    {formatPercent(franchiseSummary.dlqRate)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('prototype.franchise.summary.revenue', { defaultValue: 'Tracked revenue' })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">
                    {formatCurrency(franchiseSummary.trackedRevenueCents / 100, undefined)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('prototype.franchise.summary.matchRate', { defaultValue: 'Avg match rate' })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">
                    {formatPercent(franchiseSummary.avgMatchRate / 100)}
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-slate-200/70 bg-white/70 dark:border-slate-800/60 dark:bg-slate-900/50">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('prototype.franchise.table.franchise', { defaultValue: 'Franchise' })}</TableHead>
                      <TableHead>{t('prototype.franchise.table.channel', { defaultValue: 'Channel' })}</TableHead>
                      <TableHead className="text-right">
                        {t('prototype.franchise.table.locations', { defaultValue: 'Locations' })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('prototype.franchise.table.uploads', { defaultValue: 'Uploads' })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('prototype.franchise.table.dlqRate', { defaultValue: 'DLQ rate' })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('prototype.franchise.table.matchRate', { defaultValue: 'Match rate' })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('prototype.franchise.table.holdout', { defaultValue: 'Holdout' })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('prototype.franchise.table.revenue', { defaultValue: 'Tracked revenue' })}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {franchiseRollups.map((row) => {
                      const channelLabel = CONNECTOR_METRIC_COPY[row.channel as ConnectorType]?.defaultLabel ?? row.channel;
                      return (
                        <TableRow key={`${row.franchiseKey}-${row.channel}`}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground">{row.franchiseLabel}</span>
                              <span className="text-xs text-muted-foreground">{row.tenantIds.join(', ')}</span>
                            </div>
                          </TableCell>
                          <TableCell>{channelLabel}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.tenantIds.length)}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.totalUploads)}</TableCell>
                          <TableCell className="text-right">{formatPercent(row.dlqRate)}</TableCell>
                          <TableCell className="text-right">{formatPercent(row.avgMatchRate / 100)}</TableCell>
                          <TableCell className="text-right">{formatPercent(row.avgHoldoutPercent / 100)}</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(row.trackedRevenueCents / 100, undefined)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          {metricCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card
                key={card.label}
                className="border-emerald-100/40 bg-white/60 backdrop-blur dark:border-emerald-500/10 dark:bg-emerald-950/40"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <div className="flex flex-col gap-1">
                    <CardDescription>{card.label}</CardDescription>
                    <CardTitle className="text-3xl">{card.value}</CardTitle>
                  </div>
                  <span className="rounded-full bg-emerald-500/10 p-3 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                </CardHeader>
                <CardContent className="flex items-center justify-between pt-0">
                  <span className="text-sm text-muted-foreground">{card.trend}</span>
                  <Badge className="bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                    {card.badge}
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <Card className="border-slate-200/70 bg-white/80 shadow-lg dark:border-slate-800/60 dark:bg-slate-900/70">
            <CardHeader className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Gauge className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                <CardTitle className="text-xl">
                  {t("prototype.pipeline.title", { defaultValue: "Closed loop pipeline" })}
                </CardTitle>
              </div>
              <CardDescription>
                {t("prototype.pipeline.subtitle", {
                  defaultValue: "Monitor each stage from consent through optimization with live guardrails.",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {pipelineStages.map((stage, index) => (
                <div key={stage.key} className="flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                      <span className="text-sm font-semibold">{index + 1}</span>
                      <span className="sr-only">{stage.title}</span>
                    </div>
                    <div className="flex flex-1 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-foreground">{stage.title}</h3>
                        <Badge
                          className={cn("rounded-full px-2.5 py-1 text-xs font-semibold", statusStyles[stage.status])}
                        >
                          {t(`prototype.pipeline.status.${stage.status}`, {
                            defaultValue:
                              stage.status === "healthy"
                                ? "Healthy"
                                : stage.status === "monitoring"
                                ? "Monitoring"
                                : "Attention",
                          })}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{stage.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-dashed border-slate-200/70 bg-slate-50 px-4 py-2 text-sm dark:border-slate-700/70 dark:bg-slate-900/40">
                    <span className="text-muted-foreground">
                      {t("prototype.pipeline.metricLabel", { defaultValue: "Guardrail" })}
                    </span>
                    <span className="font-medium text-foreground">{stage.metric}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200/70 bg-gradient-to-br from-white via-white to-slate-100 shadow-lg dark:border-slate-800/60 dark:from-slate-950/70 dark:via-slate-950/60 dark:to-slate-900/60">
            <CardHeader className="space-y-4">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                <CardTitle className="text-xl">
                  {t("prototype.consent.title", { defaultValue: "Consent & identity health" })}
                </CardTitle>
              </div>
              <CardDescription>
                {t("prototype.consent.subtitle", {
                  defaultValue: "Keep capture prompts localized, accessible, and compliant across surfaces.",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2 rounded-2xl border border-emerald-200/70 bg-emerald-50 p-4 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-200">
                    {t("prototype.consent.optInRate", { defaultValue: "Opt-in rate" })}
                  </span>
                  <span className="text-sm text-emerald-700/80 dark:text-emerald-300/80">
                    {t("prototype.consent.goal", { defaultValue: "Goal = 60%" })}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-semibold text-emerald-700 dark:text-emerald-200">
                    {optInPercentLabel}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-emerald-600/70 dark:text-emerald-300/70">
                    {optInTrendLabel}
                  </span>
                </div>
                <Progress
                  value={optInProgressValue}
                  aria-label={t("prototype.consent.progressLabel", { defaultValue: "Opt-in rate progress" })}
                  aria-valuetext={optInPercentLabel}
                  className="h-2 bg-white/60 [&>div]:bg-emerald-500"
                />
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {t("prototype.consent.checklistTitle", { defaultValue: "Consent checklist" })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("prototype.consent.checklistSubtitle", {
                      defaultValue: "Strings mirrored EN/ES - screen reader copy verified",
                    })}
                  </p>
                </div>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                    <span>
                      {t("prototype.consent.items.prompts", {
                        defaultValue: "Prompt copy localized with defaultValue safeguards.",
                      })}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                    <span>
                      {t("prototype.consent.items.optOut", {
                        defaultValue: "Opt-out propagation SLA < 24h with audit log.",
                      })}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock3 className="mt-0.5 h-4 w-4 text-amber-500" aria-hidden="true" />
                    <span>
                      {t("prototype.consent.items.audit", {
                        defaultValue: "Next consent policy review due Nov 15 with legal.",
                      })}
                    </span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="grid gap-6 lg:grid-cols-[2fr_3fr]">
          <Card className="border-slate-200/70 bg-white/80 shadow-lg dark:border-slate-800/60 dark:bg-slate-900/70">
            <CardHeader className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <LineChart className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                <CardTitle className="text-xl">
                  {t("prototype.playbooks.title", { defaultValue: "Autopilot playbooks" })}
                </CardTitle>
              </div>
              <CardDescription>
                {t("prototype.playbooks.subtitle", {
                  defaultValue: "Recommended automations run when health, margin, and staffing guardrails pass.",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {playbookHighlights.map((playbook) => (
                <div
                  key={playbook.id}
                  className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 shadow-sm transition hover:border-emerald-300/60 hover:bg-emerald-50/80 dark:border-slate-800/50 dark:bg-slate-900/60 dark:hover:border-emerald-500/40 dark:hover:bg-emerald-900/40"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-base font-semibold text-foreground">{playbook.title}</h3>
                    <Badge className={cn("text-xs font-medium", playbook.badgeClassName)}>
                      {playbook.badgeLabel}
                    </Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{playbook.description}</p>
                  <p className="mt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {playbook.audience}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-slate-200/70 bg-white/90 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
            <CardHeader className="space-y-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                <CardTitle className="text-xl">
                  {t("prototype.autopilot.title", { defaultValue: "Autopilot controls" })}
                </CardTitle>
              </div>
              <CardDescription>
                {t("prototype.autopilot.subtitle", {
                  defaultValue:
                    "Manual overrides, spend guardrails, and decision logs keep automation transparent for operators.",
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
                <p className="text-sm font-semibold text-foreground">
                  {t("prototype.autopilot.modeLabel", { defaultValue: "Optimization mode" })}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300"
                    disabled
                    title={modeComingSoonLabel}
                  >
                    {t("prototype.autopilot.modes.manual", { defaultValue: "Manual" })}
                    <span className="sr-only">{modeComingSoonLabel}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300"
                    disabled
                    title={modeComingSoonLabel}
                  >
                    {t("prototype.autopilot.modes.assist", { defaultValue: "Assist" })}
                    <span className="sr-only">{modeComingSoonLabel}</span>
                  </Button>
                  <Button type="button" className="bg-emerald-500 text-white hover:bg-emerald-600" aria-pressed="true">
                    {t("prototype.autopilot.modes.autopilot", { defaultValue: "Autopilot" })}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("prototype.autopilot.modeHelp", {
                    defaultValue: "Autopilot adjusts bids daily; operators can freeze spend when guardrails dip.",
                  })}
                </p>
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {t('prototype.experiments.title', { defaultValue: 'Holdout experiments' })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('prototype.experiments.subtitle', {
                        defaultValue: 'Route a small slice of spend to control groups to measure incremental ROAS.',
                      })}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void refetchExperiments();
                    }}
                    disabled={experimentsRefreshing || experimentsLoading || experimentMutationPending}
                  >
                    {experimentsRefreshing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                    )}
                    {t('prototype.experiments.refresh', { defaultValue: 'Refresh' })}
                  </Button>
                </div>
                {experimentsLoading && !hasAutopilotExperiments ? (
                  <div className="space-y-3">
                    <Skeleton className="h-16 rounded-xl" />
                    <Skeleton className="h-16 rounded-xl" />
                  </div>
                ) : !hasAutopilotExperiments ? (
                  <p className="text-sm text-muted-foreground">
                    {t('prototype.experiments.empty', { defaultValue: 'Experiments will appear once Autopilot is provisioned for this tenant.' })}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {autopilotExperiments.map((experiment) => (
                      <div
                        key={experiment.key}
                        className="rounded-2xl border border-slate-200/60 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50"
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className="text-xs font-semibold">
                                {t(`prototype.experiments.labels.${experiment.key}` as const, {
                                  defaultValue: experiment.key,
                                })}
                              </Badge>
                              <Badge variant="secondary" className="text-xs font-semibold uppercase tracking-wide">
                                {experiment.assignment.payload?.holdout
                                  ? t('prototype.experiments.status.holdout', { defaultValue: 'Holdout active' })
                                  : t('prototype.experiments.status.enabled', { defaultValue: 'Autopilot enabled' })}
                              </Badge>
                            </div>
                            <p className="text-sm font-semibold text-foreground">{experiment.definition.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {t('prototype.experiments.holdoutShare', {
                                defaultValue: 'Holdout share: {{percent}}%',
                                percent: experiment.assignment.payload?.holdoutPercent ?? 0,
                              })}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {experiment.definition.variants.map((variant) => (
                              <Button
                                key={variant.name}
                                variant={experiment.assignment.variant === variant.name ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleExperimentVariantChange(experiment.key, variant.name)}
                                disabled={experimentMutationPending}
                                className={experiment.assignment.variant === variant.name ? 'bg-emerald-500 text-white hover:bg-emerald-600' : undefined}
                              >
                                {t(`prototype.experiments.variants.${variant.name}` as const, {
                                  defaultValue: variant.name.replace(/_/g, ' '),
                                })}
                              </Button>
                            ))}
                          </div>
                        </div>
                        {experiment.events.length > 0 ? (
                          <div className="mt-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {t('prototype.experiments.timeline.title', { defaultValue: 'Recent changes' })}
                            </p>
                            <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                              {experiment.events.slice(-3).reverse().map((event) => (
                                <li key={event.id} className="flex items-center justify-between gap-2">
                                  <span>{t(`prototype.experiments.actions.${event.action}` as const, { defaultValue: event.action })}</span>
                                  <span>{formatRelativeTime(event.createdAt)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{t("prototype.autopilot.budgetLabel", { defaultValue: "Weekly budget" })}</span>
                    <span>{budgetTargetDisplay}</span>
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-3xl font-semibold text-foreground">{budgetDeployedDisplay}</span>
                    <Badge className="bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                      {budgetBadgeDisplay}
                    </Badge>
                  </div>
                  <Progress
                    value={budgetProgressValue}
                    aria-label={t("prototype.autopilot.budgetProgress", { defaultValue: "Budget deployment progress" })}
                    className="mt-3 h-2"
                  />
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{t("prototype.autopilot.experimentLabel", { defaultValue: "Holdout lift" })}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full text-muted-foreground">
                          <Target className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">
                            {t("prototype.autopilot.experimentTooltip", {
                              defaultValue: "View experiment details",
                            })}
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t("prototype.autopilot.experimentTooltip", {
                          defaultValue: "View experiment details",
                        })}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-3xl font-semibold text-foreground">{incrementalRevenueDisplay}</span>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{incrementalRevenueCaption}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{holdoutShareLabel}</p>
                </div>
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 text-sm dark:border-slate-800/60 dark:bg-slate-900/60">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">
                    {t("prototype.autopilot.activity.title", { defaultValue: "Latest autopilot decisions" })}
                  </span>
                  <Badge className="bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                    {t("prototype.autopilot.activity.lastRun", { defaultValue: "Ran 22 minutes ago" })}
                  </Badge>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <ShieldCheck className="mt-0.5 h-4 w-4 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                    <span>
                      {t("prototype.autopilot.activity.item1", {
                        defaultValue: "Rescaled Meta prospecting by +12% after offer margin check.",
                      })}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowUpRight className="mt-0.5 h-4 w-4 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                    <span>
                      {t("prototype.autopilot.activity.item2", {
                        defaultValue: "Shifted Google budget to lunch 11:00-13:30 with 11% incremental lift.",
                      })}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock3 className="mt-0.5 h-4 w-4 text-amber-500" aria-hidden="true" />
                    <span>
                      {t("prototype.autopilot.activity.item3", {
                        defaultValue: "Paused storm-response playbook pending weather clearance and staff confirmation.",
                      })}
                    </span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>

        <ErrorBoundary
          componentName="AutopilotEducation"
          fallback={
            <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
              <div className="flex items-center gap-3">
                <Lamp className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {t("prototype.education.title", { defaultValue: "Autopilot education" })}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t("prototype.education.subtitle", {
                      defaultValue: "Explore channel-specific tips to keep automation healthy.",
                    })}
                  </p>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                {t("prototype.education.fallback", {
                  defaultValue: "Education highlights are unavailable right now. Refresh to try again.",
                })}
              </p>
            </section>
          }
        >
          <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
                  <Lamp className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {t("prototype.education.title", { defaultValue: "Autopilot education" })}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t("prototype.education.subtitle", {
                      defaultValue: "Personalized operator and manager tips by channel.",
                    })}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2"
                onClick={() =>
                  handleEducationClick({
                    id: "general.library",
                    persona: "manager",
                    connector: null,
                    severity: "info",
                    title: "",
                    description: "",
                    audience: "",
                    href: EDUCATION_DOC_LINKS.general,
                    icon: BookOpen,
                  })
                }
              >
                <BookOpen className="h-4 w-4" aria-hidden="true" />
                {t("prototype.education.libraryCta", { defaultValue: "View full library" })}
              </Button>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {educationEntries.map((entry) => {
                const Icon = entry.icon;
                return (
                  <div
                    key={entry.id}
                    className={cn(
                      "flex h-full flex-col rounded-2xl border bg-white/85 p-4 shadow-sm transition hover:shadow-md dark:bg-slate-900/70",
                      entry.severity === "warning"
                        ? "border-amber-300/70 hover:border-amber-400 dark:border-amber-400/30"
                        : "border-slate-200/70 hover:border-emerald-200/80 dark:border-slate-700/60",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={cn(
                          "flex h-9 w-9 items-center justify-center rounded-full",
                          entry.severity === "warning"
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200"
                            : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200",
                        )}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <div className="flex flex-1 flex-col gap-1">
                        <h3 className="text-base font-semibold text-foreground">{entry.title}</h3>
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {entry.audience}
                        </p>
                      </div>
                    </div>
                    <p className="mt-3 flex-1 text-sm text-muted-foreground">{entry.description}</p>
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          entry.persona === "manager"
                            ? "border-indigo-300 text-indigo-700 dark:border-indigo-500/40 dark:text-indigo-200"
                            : "border-emerald-200 text-emerald-700 dark:border-emerald-500/40 dark:text-emerald-200",
                        )}
                      >
                        {entry.persona === "manager"
                          ? t("prototype.education.persona.manager", { defaultValue: "Account managers" })
                          : t("prototype.education.persona.operator", { defaultValue: "Operators" })}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEducationClick(entry)}
                        className="gap-2"
                      >
                        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                        {entry.ctaLabel ??
                          t("prototype.education.ctaDefault", { defaultValue: "See guide" })}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </ErrorBoundary>

        <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {t('prototype.offers.title', { defaultValue: 'Offer intelligence' })}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t('prototype.offers.subtitle', {
                    defaultValue: 'Surface eligible bundles and guardrails before Autopilot deploys them.',
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void refetchOffers();
                }}
                disabled={offersBusy}
              >
                {offersBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />}
                {t('prototype.offers.refresh', { defaultValue: 'Refresh offers' })}
              </Button>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('prototype.offers.summary.weatherTitle', { defaultValue: 'Weather signal' })}
              </p>
              <p className="mt-2 text-lg font-semibold text-foreground capitalize">{offerWeatherLabel}</p>
              <p className="text-xs text-muted-foreground">
                {offerWeatherObserved}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('prototype.offers.summary.staffingTitle', { defaultValue: 'Staffing coverage' })}
              </p>
              <p className="mt-2 text-lg font-semibold text-foreground">{formattedStaffingCoverage}</p>
              <p className="text-xs text-muted-foreground">
                {staffingObserved}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('prototype.offers.summary.activeTitle', { defaultValue: 'Active offers' })}
              </p>
              <p className="mt-2 text-lg font-semibold text-foreground">{t('prototype.offers.summary.activeCount', { defaultValue: '{{count}} live', count: activeOfferCount })}</p>
              <p className="text-xs text-muted-foreground">
                {t('prototype.offers.summary.totalTracked', {
                  defaultValue: '{{count}} tracked offers',
                  count: offerSummaries.length,
                })}
              </p>
            </div>
          </div>
          <div className="mt-6 space-y-4">
            {offersBusy ? (
              <div className="space-y-3">
                <Skeleton className="h-16 rounded-2xl" />
                <Skeleton className="h-16 rounded-2xl" />
                <Skeleton className="h-16 rounded-2xl" />
              </div>
            ) : offersEmpty ? (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-200/70 bg-white/60 p-10 text-center text-sm text-muted-foreground dark:border-slate-800/60 dark:bg-slate-900/60">
                <Sparkles className="h-8 w-8 text-emerald-500" aria-hidden="true" />
                <div className="space-y-1">
                  <p className="text-base font-medium text-foreground">
                    {t('prototype.offers.empty.title', { defaultValue: 'No offer diagnostics yet' })}
                  </p>
                  <p>{t('prototype.offers.empty.subtitle', { defaultValue: 'Enable heuristics bundles to populate offer intelligence.' })}</p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/4">{t('prototype.offers.table.offer', { defaultValue: 'Offer' })}</TableHead>
                      <TableHead className="w-1/6">{t('prototype.offers.table.channel', { defaultValue: 'Channel' })}</TableHead>
                      <TableHead className="w-1/6">{t('prototype.offers.table.margin', { defaultValue: 'Margin' })}</TableHead>
                      <TableHead className="w-1/6">{t('prototype.offers.table.score', { defaultValue: 'Eligibility' })}</TableHead>
                      <TableHead>{t('prototype.offers.table.signals', { defaultValue: 'Signals & guardrails' })}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topOffers.map((offer) => {
                      const eligibilityPercent = Math.min(Math.max(offer.eligibilityScore, 0), 1);
                      const conflicts = offer.conflicts.length > 0 ? offer.conflicts.join(', ') : null;
                      const segments =
                        offer.signals.demandSegments && offer.signals.demandSegments.length > 0
                          ? offer.signals.demandSegments.join(', ')
                          : null;
                      return (
                        <TableRow key={offer.id} className="align-top">
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-foreground">{offer.name}</span>
                                <Badge className={cn('text-xs font-medium', offerStateBadgeStyles[offer.state])}>
                                  {t(`prototype.offers.states.${offer.state}` as const, {
                                    defaultValue:
                                      offer.state === 'enabled'
                                        ? 'Enabled'
                                        : offer.state === 'snoozed'
                                          ? 'Snoozed'
                                          : 'Disabled',
                                  })}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{offer.reason ?? t('prototype.offers.table.defaultReason', { defaultValue: 'Heuristic generated recommendation' })}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={cn('text-xs', channelBadgeStyles[offer.channel])}>
                              {offer.channel === 'google_ads' ? 'Google' : 'Meta'}
                            </Badge>
                            <p className="mt-1 text-xs text-muted-foreground">{formatCurrency(offer.priceCents / 100)}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-semibold text-foreground">
                              {formatPercent(offer.marginPercent, { maximumFractionDigits: 0 })}
                            </p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-semibold text-foreground">
                              {formatPercent(eligibilityPercent, { maximumFractionDigits: 0 })}
                            </p>
                            <Progress
                              value={eligibilityPercent * 100}
                              aria-label={t('prototype.offers.table.eligibilityProgress', {
                                defaultValue: 'Eligibility score',
                              })}
                              className="mt-2 h-1.5"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                              {offer.signals.weatherSeverity ? (
                                <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                                  {t('prototype.offers.signals.weather', {
                                    defaultValue: 'Weather: {{severity}}',
                                    severity: offer.signals.weatherSeverity.replace(/_/g, ' '),
                                  })}
                                </Badge>
                              ) : null}
                              {offer.signals.staffingCoverageRatio !== null &&
                              offer.signals.staffingCoverageRatio !== undefined ? (
                                <Badge variant="secondary" className="bg-sky-500/10 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
                                  {t('prototype.offers.signals.staffing', {
                                    defaultValue: 'Staffing: {{coverage}} coverage',
                                    coverage: formatPercent(offer.signals.staffingCoverageRatio, { maximumFractionDigits: 0 }),
                                  })}
                                </Badge>
                              ) : null}
                              {segments ? (
                                <Badge variant="secondary" className="bg-purple-500/10 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300">
                                  {t('prototype.offers.signals.segments', {
                                    defaultValue: 'Segments: {{segments}}',
                                    segments,
                                  })}
                                </Badge>
                              ) : null}
                              {conflicts ? (
                                <Badge variant="secondary" className="bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                                  {t('prototype.offers.signals.conflicts', {
                                    defaultValue: 'Conflicts: {{conflicts}}',
                                    conflicts,
                                  })}
                                </Badge>
                              ) : null}
                              {!offer.signals.weatherSeverity &&
                              offer.signals.staffingCoverageRatio === null &&
                              !segments &&
                              !conflicts ? (
                                <span>{t('prototype.offers.table.noSignals', { defaultValue: 'No active guardrails' })}</span>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <Gauge className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {t('prototype.rollups.title', { defaultValue: 'Analytics rollups' })}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t('prototype.rollups.subtitle', {
                    defaultValue: 'Download daily spend, match rate, and holdout metrics for CX and finance teams.',
                  })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void refetchRollups();
                }}
                disabled={rollupsRefreshing || rollupsLoading}
              >
                {rollupsRefreshing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t('prototype.rollups.refresh', { defaultValue: 'Refresh' })}
              </Button>
              <Button
                size="sm"
                onClick={handleRollupDownload}
                disabled={rollupMutationPending}
                className="bg-emerald-500 text-white hover:bg-emerald-600"
              >
                {rollupMutationPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ArrowUpRight className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t('prototype.rollups.download', { defaultValue: 'Download CSV' })}
              </Button>
            </div>
          </div>
          <Separator className="my-4" />
          {rollupsLoading && rollups.length === 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-24 rounded-2xl" />
              <Skeleton className="h-24 rounded-2xl" />
            </div>
          ) : rollups.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('prototype.rollups.empty', { defaultValue: 'Rollups will appear after Autopilot processes daily uploads.' })}
            </p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('prototype.rollups.metrics.totalUploads', { defaultValue: 'Uploads (7 days)' })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">{formatNumber(totalRollupUploads)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('prototype.rollups.metrics.avgMatch', { defaultValue: 'Avg. match rate' })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">
                    {formatPercent(avgRollupMatchRate / 100)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('prototype.rollups.metrics.holdout', { defaultValue: 'Avg. holdout' })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">
                    {formatPercent(avgHoldoutPercent / 100)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 text-sm text-muted-foreground dark:border-slate-800/60 dark:bg-slate-900/50">
                  {latestRollupExport ? (
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-wide">
                        {t('prototype.rollups.latestExport', { defaultValue: 'Latest export' })}
                      </p>
                      <p className="font-semibold text-foreground">
                        {formatRelativeTime(latestRollupExport.createdAt)}
                      </p>
                      <p>
                        {t('prototype.rollups.latestExportRows', {
                          defaultValue: '{{count}} rows · {{format}}',
                          count: formatNumber(latestRollupExport.rowCount),
                          format: latestRollupExport.format.toUpperCase(),
                        })}
                      </p>
                    </div>
                  ) : (
                    <p>
                      {t('prototype.rollups.latestExportEmpty', {
                        defaultValue: 'Exports will be logged here for audit purposes.',
                      })}
                    </p>
                  )}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('prototype.rollups.exports.title', { defaultValue: 'Export history' })}
                </p>
                {rollupExports.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('prototype.rollups.exports.empty', { defaultValue: 'No exports recorded yet.' })}
                  </p>
                ) : (
                  <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                    {rollupExports.slice(0, 5).map((entry) => (
                      <li key={entry.id} className="flex items-center justify-between">
                        <span>
                          {formatRelativeTime(entry.createdAt)} · {entry.format.toUpperCase()} · {formatNumber(entry.rowCount)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {t('prototype.optimization.title', { defaultValue: 'Optimization engine' })}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t('prototype.optimization.subtitle', {
                    defaultValue: 'Let heuristics rebalance spend and offers while keeping a tight audit trail.',
                  })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void Promise.all([refetchHeuristics(), refetchOptimization()]);
                }}
                disabled={heuristicsBusy || optimizationBusy}
              >
                {heuristicsBusy || optimizationBusy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t('prototype.optimization.refresh', { defaultValue: 'Refresh' })}
              </Button>
              <div className="flex items-center gap-2" aria-live="polite" aria-busy={heuristicsMutationPending ? 'true' : undefined}>
                <Switch
                  checked={heuristicsActive}
                  onCheckedChange={handleHeuristicsToggle}
                  disabled={heuristicsMutationPending}
                  id={heuristicsToggleId}
                  aria-describedby={heuristicsToggleStatusId}
                  aria-label={t('prototype.optimization.killSwitchLabel', { defaultValue: 'Optimization engine' })}
                />
                <span id={heuristicsToggleStatusId} className="text-sm text-muted-foreground">
                  {heuristicsStatusLabel}
                </span>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          {heuristicsSectionBusy ? (
            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3" aria-busy="true">
              <Skeleton className="h-40 rounded-2xl lg:col-span-2 xl:col-span-1" />
              <Skeleton className="h-56 rounded-2xl" />
              <Skeleton className="h-56 rounded-2xl" />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3" aria-live="polite">
              <div
                className="space-y-4 rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50 lg:col-span-2 xl:col-span-1"
                role="status"
                aria-live="polite"
              >
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-2xl border border-emerald-200/60 bg-emerald-50/60 p-4 dark:border-emerald-500/20 dark:bg-emerald-950/50">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                      {t('prototype.optimization.metrics.incrementalRoas', { defaultValue: 'Incremental ROAS' })}
                    </p>
                    <p className="mt-2 text-3xl font-semibold text-emerald-700 dark:text-emerald-200">
                      {optimizationMetrics ? `${formatNumber(optimizationMetrics.incrementalRoas, { maximumFractionDigits: 2 })}×` : '-'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t('prototype.optimization.metrics.costPerNewDiner', { defaultValue: 'Cost per new diner' })}
                    </p>
                    <p className="mt-2 text-3xl font-semibold text-foreground">
                      {optimizationMetrics ? formatCurrency(optimizationMetrics.costPerNewDiner, undefined) : '-'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/60">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t('prototype.optimization.metrics.lag', { defaultValue: 'Avg decision lag' })}
                    </p>
                    <p className="mt-2 text-3xl font-semibold text-foreground">
                      {optimizationMetrics ? `${formatNumber(optimizationMetrics.averageLagMinutes)} min` : '-'}
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 text-sm text-muted-foreground dark:border-slate-800/60 dark:bg-slate-900/60">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('prototype.optimization.metrics.offerStates', { defaultValue: 'Offer states' })}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {(['enabled', 'disabled', 'snoozed'] as const).map((state) => (
                      <Badge key={state} className="bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                        {t(`prototype.optimization.offerStates.${state}` as const, {
                          defaultValue: state === 'enabled' ? 'Enabled' : state === 'disabled' ? 'Disabled' : 'Snoozed',
                        })}
                        {': '}
                        {formatNumber(offerStateCounts[state] ?? 0)}
                      </Badge>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    {optimizationLastUpdatedAt
                      ? t('prototype.optimization.metrics.lastUpdated', {
                          defaultValue: 'Last updated {{timeAgo}}',
                          timeAgo: formatRelativeTime(optimizationLastUpdatedAt),
                        })
                      : t('prototype.optimization.metrics.lastUpdatedFallback', {
                          defaultValue: 'Awaiting first optimization run',
                        })}
                  </p>
                </div>
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('prototype.optimization.rules.title', { defaultValue: 'Rule health' })}
                  </p>
                  {heuristicsRefreshing ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" /> : null}
                </div>
                {heuristicsRules.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('prototype.optimization.rules.empty', { defaultValue: 'No heuristic rules configured yet.' })}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {heuristicsRules.slice(0, 4).map((summary) => {
                      const lastRun = summary.lastRun;
                      const resultKey = lastRun?.result ?? 'skipped';
                      return (
                        <div
                          key={summary.rule.id}
                          className="rounded-2xl border border-slate-200/70 bg-white/80 p-4 dark:border-slate-800/60 dark:bg-slate-900/70"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-foreground">{summary.rule.ruleKey}</p>
                              <p className="text-xs text-muted-foreground">
                                {t('prototype.optimization.rules.lastRun', { defaultValue: 'Last run' })}{' '}
                                {lastRun ? formatRelativeTime(lastRun.createdAt) : t('prototype.optimization.rules.never', { defaultValue: 'Never run' })}
                              </p>
                            </div>
                            <Badge className={cn('text-xs font-medium', heuristicResultStyles[resultKey])}>
                              {heuristicResultLabels[resultKey]}
                            </Badge>
                          </div>
                          <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
                            <span>
                              {t('prototype.optimization.rules.appliedCount', { defaultValue: 'Applied' })}: {formatNumber(summary.appliedCount)}
                            </span>
                            <span>
                              {t('prototype.optimization.rules.skippedCount', { defaultValue: 'Skipped' })}: {formatNumber(summary.skippedCount)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-3 rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('prototype.optimization.runs.title', { defaultValue: 'Recent runs' })}
                  </p>
                  {optimizationRefreshing ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" /> : null}
                </div>
                {optimizationRuns.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('prototype.optimization.runs.empty', { defaultValue: 'No heuristic runs in the last day.' })}
                  </p>
                ) : (
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {optimizationRuns.slice(0, 5).map((run) => {
                      const resultKey = run.result;
                      return (
                        <li
                          key={run.id}
                          className="flex items-center justify-between rounded-xl border border-slate-200/70 bg-white/80 px-3 py-2 dark:border-slate-800/60 dark:bg-slate-900/70"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-foreground">
                              {run.message ?? t('prototype.optimization.runs.defaultMessage', { defaultValue: 'Rule evaluation' })}
                            </span>
                            <span className="text-xs text-muted-foreground">{formatRelativeTime(run.createdAt)}</span>
                          </div>
                          <Badge className={cn('text-xs', heuristicResultStyles[resultKey])}>{heuristicResultLabels[resultKey]}</Badge>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <PiggyBank className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {t("prototype.budgets.title", { defaultValue: "Manual budget recommendations" })}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("prototype.budgets.subtitle", {
                    defaultValue: "Review Autopilot suggestions, apply overrides, and keep a paper trail for finance.",
                  })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void refetchBudgets();
                }}
                disabled={budgetsRefreshing || budgetsLoading || budgetMutationPending}
              >
                {budgetsRefreshing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t("prototype.budgets.refresh", { defaultValue: "Refresh" })}
              </Button>
            </div>
          </div>
          <Separator className="my-4" />
          {budgetsLoading && !hasBudgetRecommendations ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <Skeleton className="h-40 rounded-2xl" />
              <Skeleton className="h-40 rounded-2xl" />
              <Skeleton className="h-40 rounded-2xl" />
            </div>
          ) : !hasBudgetRecommendations ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200/70 bg-white/60 p-10 text-center text-sm text-muted-foreground backdrop-blur dark:border-slate-800/60 dark:bg-slate-900/60">
              <PiggyBank className="h-8 w-8 text-emerald-500" aria-hidden="true" />
              <div className="space-y-1">
                <p className="text-base font-medium text-foreground">
                  {t("prototype.budgets.empty.title", { defaultValue: "No recommendations yet" })}
                </p>
                <p>
                  {t("prototype.budgets.empty.subtitle", {
                    defaultValue: "Autopilot will surface manual overrides once enough lift data is captured.",
                  })}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void refetchBudgets();
                }}
              >
                {t("prototype.budgets.empty.cta", { defaultValue: "Check again" })}
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              {budgetRecommendations.map(({ recommendation, events }) => {
                const campaign = (recommendation.metadata as { campaign?: string } | undefined)?.campaign;
                const delta = recommendation.deltaPercent ?? 0;
                const deltaFormatted = formatPercent(Math.abs(delta), {
                  style: "percent",
                  maximumFractionDigits: 0,
                });
                const isIncrease = delta >= 0;
                const statusLabel = t(`prototype.budgets.status.${recommendation.status}` as const, {
                  defaultValue: recommendation.status,
                });
                const latestEvents = events.slice(0, 3);
                const recommendedBudget = formatBudgetCents(recommendation.recommendedBudgetCents);
                const currentBudget = formatBudgetCents(recommendation.currentBudgetCents);
                const appliedBudget = recommendation.appliedBudgetCents ? formatBudgetCents(recommendation.appliedBudgetCents) : null;
                const disableActions = budgetMutationPending || recommendation.status === 'dismissed';

                return (
                  <div
                    key={recommendation.id}
                    className="rounded-3xl border border-slate-200/70 bg-white/80 p-5 shadow-md dark:border-slate-800/60 dark:bg-slate-900/70"
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className={cn("text-xs font-semibold", channelBadgeStyles[recommendation.channel])}>
                            {t(`prototype.budgets.channel.${recommendation.channel}` as const, {
                              defaultValue: recommendation.channel.replace(/_/g, " "),
                            })}
                          </Badge>
                          <Badge className={cn("text-xs font-medium", budgetStatusStyles[recommendation.status])}>
                            {statusLabel}
                          </Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">
                          {campaign ?? recommendation.recommendationType}
                        </h3>
                        <p className="text-sm text-muted-foreground">{recommendation.reason ?? t("prototype.budgets.reasonFallback", { defaultValue: "Autopilot generated this recommendation." })}</p>
                      </div>
                      <div className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-2">
                        <div className="rounded-2xl border border-slate-200/60 bg-white/60 p-3 text-center dark:border-slate-800/60 dark:bg-slate-900/50">
                          <p className="text-xs uppercase tracking-wide">
                            {t("prototype.budgets.cards.current", { defaultValue: "Current" })}
                          </p>
                          <p className="mt-1 text-base font-semibold text-foreground">{currentBudget}</p>
                        </div>
                        <div className="rounded-2xl border border-emerald-200/60 bg-emerald-50/60 p-3 text-center dark:border-emerald-500/60 dark:bg-emerald-950/40">
                          <p className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                            {t("prototype.budgets.cards.recommended", { defaultValue: "Recommended" })}
                          </p>
                          <p className="mt-1 text-base font-semibold text-emerald-700 dark:text-emerald-300">{recommendedBudget}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-200/60 bg-white/60 p-3 text-center dark:border-slate-800/60 dark:bg-slate-900/50">
                          <p className="text-xs uppercase tracking-wide">
                            {t("prototype.budgets.cards.delta", { defaultValue: "Delta" })}
                          </p>
                          <p
                            className={cn(
                              "mt-1 text-base font-semibold",
                              isIncrease ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300",
                            )}
                          >
                            {isIncrease ? "+" : "-"}
                            {deltaFormatted}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-slate-200/60 bg-white/60 p-3 text-center dark:border-slate-800/60 dark:bg-slate-900/50">
                          <p className="text-xs uppercase tracking-wide">
                            {t("prototype.budgets.cards.confidence", { defaultValue: "Confidence" })}
                          </p>
                          <p className="mt-1 text-base font-semibold text-foreground">
                            {recommendation.confidence !== null && recommendation.confidence !== undefined
                              ? formatPercent(recommendation.confidence, {
                                  style: "percent",
                                  maximumFractionDigits: 0,
                                })
                              : "--"}
                          </p>
                        </div>
                        {appliedBudget ? (
                          <div className="rounded-2xl border border-slate-200/60 bg-white/60 p-3 text-center dark:border-slate-800/60 dark:bg-slate-900/50 sm:col-span-2">
                            <p className="text-xs uppercase tracking-wide">
                              {t("prototype.budgets.cards.applied", { defaultValue: "Applied budget" })}
                            </p>
                            <p className="mt-1 text-base font-semibold text-foreground">{appliedBudget}</p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-500 text-white hover:bg-emerald-600"
                          onClick={() =>
                            handleBudgetAction(recommendation.id, 'applied', {
                              targetBudgetCents: recommendation.recommendedBudgetCents,
                              note: t("prototype.budgets.actions.applyNote", { defaultValue: "Applied via cockpit" }),
                            })
                          }
                          disabled={disableActions || recommendation.status === 'applied'}
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
                          {t("prototype.budgets.actions.apply", { defaultValue: "Apply" })}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleBudgetAction(recommendation.id, 'dismissed', {
                              note: t("prototype.budgets.actions.dismissNote", { defaultValue: "Dismissed after review" }),
                            })
                          }
                          disabled={disableActions || recommendation.status === 'dismissed'}
                        >
                          <Ban className="mr-2 h-4 w-4" aria-hidden="true" />
                          {t("prototype.budgets.actions.dismiss", { defaultValue: "Dismiss" })}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            handleBudgetAction(recommendation.id, 'snoozed', {
                              note: t("prototype.budgets.actions.snoozeNote", { defaultValue: "Snoozed for tomorrow" }),
                              scheduledFor: new Date(Date.now() + 24 * 60 * 60 * 1000),
                            })
                          }
                          disabled={disableActions}
                        >
                          <Clock3 className="mr-2 h-4 w-4" aria-hidden="true" />
                          {t("prototype.budgets.actions.snooze", { defaultValue: "Snooze" })}
                        </Button>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t("prototype.budgets.timeline.title", { defaultValue: "Audit log" })}
                        </p>
                        {latestEvents.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            {t("prototype.budgets.timeline.empty", { defaultValue: "No activity recorded." })}
                          </p>
                        ) : (
                          <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                            {latestEvents.map((event) => (
                              <li key={event.id} className="flex items-center justify-between gap-2">
                                <span>{budgetActionLabels[event.action] ?? event.action}</span>
                                <span>{formatRelativeTime(event.createdAt)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {t("prototype.segmentation.title", { defaultValue: "Segmentation & suppression health" })}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("prototype.segmentation.subtitle", {
                    defaultValue: "Score guests nightly and sync opt-outs before Autopilot pushes budget updates.",
                  })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {segmentationRun ? (
                <Badge className={cn("text-xs font-medium", segmentationStatusStyles[segmentationRun.status])}>
                  {t(`prototype.segmentation.status.${segmentationRun.status}` as const, {
                    defaultValue:
                      segmentationRun.status === "succeeded"
                        ? t("prototype.segmentation.status.succeeded", { defaultValue: "Succeeded" })
                        : segmentationRun.status === "running"
                          ? t("prototype.segmentation.status.running", { defaultValue: "Running" })
                          : segmentationRun.status === "failed"
                            ? t("prototype.segmentation.status.failed", { defaultValue: "Failed" })
                            : t("prototype.segmentation.status.queued", { defaultValue: "Queued" }),
                  })}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs font-medium">
                  {t("prototype.segmentation.status.never", { defaultValue: "Not yet run" })}
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void refetchSegmentation();
                }}
                disabled={segmentationRefreshing || segmentationLoading || triggerSegmentationMutation.isPending}
              >
                {segmentationRefreshing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t("prototype.segmentation.refresh", { defaultValue: "Refresh view" })}
              </Button>
              <Button
                size="sm"
                onClick={() => handleSegmentationRun("sync")}
                disabled={triggerSegmentationMutation.isPending}
              >
                {triggerSegmentationMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t("prototype.segmentation.runCta", { defaultValue: "Run segmentation" })}
              </Button>
            </div>
          </div>
          <Separator className="my-4" />
          {segmentationLoading && !hasSegmentationData ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Skeleton className="h-28 rounded-2xl" />
              <Skeleton className="h-28 rounded-2xl" />
              <Skeleton className="h-28 rounded-2xl" />
              <Skeleton className="h-28 rounded-2xl" />
            </div>
          ) : !hasSegmentationData ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200/70 bg-white/60 p-10 text-center text-sm text-muted-foreground backdrop-blur dark:border-slate-800/60 dark:bg-slate-900/60">
              <Users className="h-8 w-8 text-emerald-500" aria-hidden="true" />
              <div className="space-y-1">
                <p className="text-base font-medium text-foreground">
                  {t("prototype.segmentation.empty.title", { defaultValue: "No segmentation runs yet" })}
                </p>
                <p>
                  {t("prototype.segmentation.empty.subtitle", {
                    defaultValue: "Kick off a baseline run to generate RFM tiers and suppression audits.",
                  })}
                </p>
              </div>
              <Button onClick={() => handleSegmentationRun("sync")} disabled={triggerSegmentationMutation.isPending}>
                {triggerSegmentationMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t("prototype.segmentation.empty.cta", { defaultValue: "Start segmentation" })}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-sm text-muted-foreground">
                    {t("prototype.segmentation.metrics.profiles", { defaultValue: "Profiles scored" })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">{formatNumber(totalProfilesScored)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {segmentationRunTimestamp
                      ? t("prototype.segmentation.metrics.lastRun", {
                          defaultValue: "Last run {{timeAgo}}",
                          timeAgo: formatRelativeTime(segmentationRunTimestamp),
                        })
                      : t("prototype.segmentation.metrics.lastRunFallback", { defaultValue: "Pending first run" })}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-sm text-muted-foreground">
                    {t("prototype.segmentation.metrics.suppressed", { defaultValue: "Suppressed in sync" })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">
                    {formatPercent(suppressedShare)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("prototype.segmentation.metrics.suppressedDetail", {
                      defaultValue: "{{count}} contacts suppressed",
                      count: formatNumber(totalSuppressedProfiles),
                    })}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {t("prototype.segmentation.metrics.mismatches", { defaultValue: "Suppression mismatches" })}
                    </p>
                    {totalMismatchProfiles > 0 ? (
                      <Badge variant="destructive" className="text-xs font-semibold uppercase tracking-wide">
                        {t("prototype.segmentation.metrics.mismatchFlag", { defaultValue: "Action needed" })}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="mt-2 text-3xl font-semibold text-foreground">
                    {formatPercent(mismatchShare)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("prototype.segmentation.metrics.mismatchesDetail", {
                      defaultValue: "{{count}} profiles across {{channels}} channels",
                      count: formatNumber(totalMismatchProfiles),
                      channels: formatNumber(mismatchChannelsWithIssues),
                    })}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <p className="text-sm text-muted-foreground">
                    {t("prototype.segmentation.metrics.segmentCount", { defaultValue: "Active segments" })}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-foreground">
                    {formatNumber(aggregatedSegments.length)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("prototype.segmentation.metrics.segmentDetail", {
                      defaultValue: "Top {{count}} highlighted below",
                      count: formatNumber(Math.min(topSegments.length, 5)),
                    })}
                  </p>
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      {t("prototype.segmentation.table.title", { defaultValue: "Top segments" })}
                    </p>
                    <Badge variant="outline" className="text-xs font-medium">
                      {t("prototype.segmentation.table.segmentsCount", {
                        defaultValue: "{{count}} segments scored",
                        count: formatNumber(aggregatedSegments.length),
                      })}
                    </Badge>
                  </div>
                  {topSegments.length === 0 ? (
                    <p className="mt-4 text-sm text-muted-foreground">
                      {t("prototype.segmentation.table.empty", { defaultValue: "Segments will appear after the next run." })}
                    </p>
                  ) : (
                    <div className="mt-4 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="text-xs uppercase tracking-wide text-muted-foreground">
                            <TableHead>{t("prototype.segmentation.table.segment", { defaultValue: "Segment" })}</TableHead>
                            <TableHead className="text-right">
                              {t("prototype.segmentation.table.profiles", { defaultValue: "Profiles" })}
                            </TableHead>
                            <TableHead className="text-right">
                              {t("prototype.segmentation.table.share", { defaultValue: "Share" })}
                            </TableHead>
                            <TableHead className="text-right">
                              {t("prototype.segmentation.table.recency", { defaultValue: "Avg recency" })}
                            </TableHead>
                            <TableHead className="text-right">
                              {t("prototype.segmentation.table.frequency", { defaultValue: "Avg frequency" })}
                            </TableHead>
                            <TableHead className="text-right">
                              {t("prototype.segmentation.table.monetary", { defaultValue: "Avg monetary" })}
                            </TableHead>
                            <TableHead className="text-right">
                              {t("prototype.segmentation.table.suppressed", { defaultValue: "Suppressed" })}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {topSegments.map((entry) => (
                            <TableRow key={entry.segment} className="text-sm">
                              <TableCell className="font-medium text-foreground">
                                {t(`prototype.segmentation.labels.${entry.segment}` as const, {
                                  defaultValue: entry.segment.replace(/_/g, " "),
                                })}
                              </TableCell>
                              <TableCell className="text-right">{formatNumber(entry.count)}</TableCell>
                              <TableCell className="text-right">
                                {formatPercent(entry.share, { style: "percent", maximumFractionDigits: 1 })}
                              </TableCell>
                              <TableCell className="text-right">
                                {t("prototype.segmentation.table.daysValue", {
                                  defaultValue: "{{value}} days",
                                  value: formatNumber(Math.round(entry.avgRecencyDays), {
                                    formatOptions: { maximumFractionDigits: 0 },
                                  }),
                                })}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(entry.avgFrequencyScore, {
                                  formatOptions: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
                                })}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(entry.avgMonetaryScore, {
                                  formatOptions: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
                                })}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatPercent(
                                  entry.count > 0 ? entry.suppressedCount / entry.count : 0,
                                  { style: "percent", maximumFractionDigits: 1 },
                                )}
                                <span className="ml-1 text-xs text-muted-foreground">
                                  ({formatNumber(entry.suppressedCount)})
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      {t("prototype.segmentation.audits.title", { defaultValue: "Suppression audits" })}
                    </p>
                    <Badge variant="outline" className="text-xs font-medium">
                      {t("prototype.segmentation.audits.channels", {
                        defaultValue: "{{count}} channels reviewed",
                        count: formatNumber(suppressionAuditByChannel.length),
                      })}
                    </Badge>
                  </div>
                  {suppressionAuditByChannel.length === 0 ? (
                    <p className="mt-4 text-sm text-muted-foreground">
                      {t("prototype.segmentation.audits.empty", {
                        defaultValue: "Run segmentation to populate suppression audits.",
                      })}
                    </p>
                  ) : (
                    <div className="mt-4 space-y-4">
                      {suppressionAuditByChannel.map((audit) => (
                        <div
                          key={audit.channel}
                          className="rounded-xl border border-slate-200/70 bg-white/80 p-3 dark:border-slate-800/60 dark:bg-slate-900/60"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <Badge variant="secondary" className="text-xs font-semibold capitalize">
                              {t(`prototype.segmentation.channels.${audit.channel}` as const, {
                                defaultValue: audit.channel.toUpperCase(),
                              })}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {segmentationRunTimestamp
                                ? t("prototype.segmentation.audits.updated", {
                                    defaultValue: "Updated {{timeAgo}}",
                                    timeAgo: formatRelativeTime(segmentationRunTimestamp),
                                  })
                                : t("prototype.segmentation.audits.updatedFallback", { defaultValue: "Awaiting run" })}
                            </span>
                          </div>
                          <dl className="mt-3 grid grid-cols-2 gap-3 text-sm text-muted-foreground">
                            <div>
                              <dt>{t("prototype.segmentation.audits.loyalty", { defaultValue: "Loyalty contacts" })}</dt>
                              <dd className="font-semibold text-foreground">{formatNumber(audit.loyaltyContacts)}</dd>
                            </div>
                            <div>
                              <dt>{t("prototype.segmentation.audits.suppressed", { defaultValue: "Suppressed" })}</dt>
                              <dd className="font-semibold text-foreground">{formatNumber(audit.suppressedContacts)}</dd>
                            </div>
                            <div>
                              <dt>{t("prototype.segmentation.audits.mismatches", { defaultValue: "Mismatches" })}</dt>
                              <dd className="font-semibold text-foreground">
                                {formatNumber(audit.mismatchContacts)}
                              </dd>
                            </div>
                            <div>
                              <dt>{t("prototype.segmentation.audits.sample", { defaultValue: "Sample value" })}</dt>
                              <dd className="font-semibold text-foreground">
                                {audit.mismatchExamples.length > 0 ? audit.mismatchExamples[0] : t("prototype.segmentation.audits.sampleFallback", { defaultValue: "-" })}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-800/60 dark:bg-slate-900/80">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className="h-5 w-5 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {t("prototype.connectors.title", { defaultValue: "Connector diagnostics" })}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("prototype.connectors.subtitle", {
                    defaultValue: "Track batch health, rejection reasons, and replay queues across platforms.",
                  })}
                </p>
              </div>
            </div>
            <Badge className="bg-slate-900 text-white dark:bg-emerald-500 dark:text-emerald-950">
              {t("prototype.connectors.sla", { defaultValue: "SLA: Upload = 1 hour" })}
            </Badge>
          </div>
          <Separator className="my-4" />
          {diagnosticsLoading && !hasConnectorData ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-11 w-full rounded-full" />
              <Skeleton className="h-36 w-full rounded-2xl" />
              <Skeleton className="h-36 w-full rounded-2xl" />
            </div>
          ) : !hasConnectorData ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200/70 bg-white/60 p-10 text-center text-sm text-muted-foreground backdrop-blur dark:border-slate-800/60 dark:bg-slate-900/60">
              <ShieldCheck className="h-8 w-8 text-emerald-500" aria-hidden="true" />
              <div className="space-y-1">
                <p className="text-base font-medium text-foreground">
                  {t("prototype.connectors.empty.title", { defaultValue: "No sandbox uploads yet" })}
                </p>
                <p>
                  {t("prototype.connectors.empty.subtitle", {
                    defaultValue: "Run a canary upload to populate diagnostics.",
                  })}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={refreshConnectorData} disabled={uploadsRefreshing}>
                {uploadsRefreshing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t("prototype.connectors.refresh", { defaultValue: "Refresh diagnostics" })}
              </Button>
            </div>
          ) : (
            <Tabs defaultValue={connectorTabs[0]?.value ?? "queue"} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <TabsList className="inline-flex h-11 items-center gap-2 rounded-full bg-slate-100/70 p-1 text-sm dark:bg-slate-900/70">
                  {connectorTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="rounded-full px-4 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-md"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" className="self-start lg:self-auto" onClick={refreshConnectorData} disabled={uploadsRefreshing}>
                    {uploadsRefreshing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                    )}
                    {t("prototype.connectors.refresh", { defaultValue: "Refresh diagnostics" })}
                  </Button>
                </div>
              </div>
              {connectorTabs.map((tab) => (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="rounded-2xl border border-slate-200/70 bg-white/60 p-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-900/60"
                >
                  {tab.isConnector ? (
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-foreground">{tab.label}</span>
                              <Badge className={cn("text-xs font-medium", statusStyles[tab.status])}>{tab.health}</Badge>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                              <span>
                                {tab.lastSuccessAt
                                  ? t("prototype.connectors.lastSuccess", {
                                      defaultValue: "Last success {{timeAgo}}",
                                      timeAgo: formatRelativeTime(tab.lastSuccessAt),
                                    })
                                  : t("prototype.connectors.lastSuccessFallback", {
                                      defaultValue: "Awaiting first sandbox upload",
                                    })}
                              </span>
                              {tab.lastFailureAt ? (
                                <span>
                                  {t("prototype.connectors.lastFailure", {
                                    defaultValue: "Last failure {{timeAgo}}",
                                    timeAgo: formatRelativeTime(tab.lastFailureAt),
                                  })}
                                </span>
                              ) : null}
                              <span>
                                {t("prototype.connectors.totalUploads", {
                                  defaultValue: "{{count}} uploads observed",
                                  count: formatNumber(tab.totalUploads ?? 0),
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col items-start gap-2 md:items-end">
                            <div className="flex items-center gap-2">
                              <Switch
                                id={`autopilot-${tab.value}-toggle`}
                                checked={Boolean(tab.enabled)}
                                disabled={diagnosticsLoading || !tenantSlug || toggleConnectorMutation.isPending}
                                onCheckedChange={(checked) =>
                                  handleToggleConnector(tab.value as ConnectorType, "sandbox", checked)
                                }
                                aria-label={t("prototype.connectors.toggle.sandbox", {
                                  defaultValue: "Toggle sandbox connector",
                                })}
                              />
                              <label htmlFor={`autopilot-${tab.value}-toggle`} className="text-xs text-muted-foreground">
                                {t("prototype.connectors.toggle.sandbox", { defaultValue: "Sandbox connector" })}
                              </label>
                              {pendingToggleConnector === tab.value && toggleConnectorMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
                              ) : null}
                            </div>
                            <Badge variant="outline" className="text-xs font-medium">
                              {t(`prototype.connectors.credentialStatus.${tab.credentialStatus ?? "none"}` as const, {
                                defaultValue:
                                  tab.credentialStatus === "active"
                                    ? t("prototype.connectors.credentialStatus.active", {
                                        defaultValue: "Credentials active",
                                      })
                                    : tab.credentialStatus === "staged"
                                      ? t("prototype.connectors.credentialStatus.staged", {
                                          defaultValue: "Credentials staged",
                                        })
                                      : tab.credentialStatus === "revoked"
                                        ? t("prototype.connectors.credentialStatus.revoked", {
                                            defaultValue: "Credentials revoked",
                                          })
                                        : t("prototype.connectors.credentialStatus.none", {
                                            defaultValue: "Credentials missing",
                                          }),
                              })}
                            </Badge>
                          </div>
                        </div>
                        {!tab.hasSandboxData ? (
                          <p className="mt-3 text-xs text-muted-foreground">
                            {t("prototype.connectors.sandboxEmpty", {
                              defaultValue: "Seed sandbox data to activate diagnostics.",
                            })}
                          </p>
                        ) : null}
                      </div>

                      <div className="space-y-3 rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                        <div className="flex flex-col gap-1 pb-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              {t("prototype.connectors.table.recentHeading", { defaultValue: "Recent uploads" })}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t("prototype.connectors.table.recentSubtitle", {
                                defaultValue: "Includes the latest sandbox batches for this connector.",
                              })}
                            </p>
                          </div>
                          {tab.uploadsRefreshing ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
                          ) : null}
                        </div>
                        {tab.uploadsLoading ? (
                          <Skeleton className="h-40 w-full rounded-xl" />
                        ) : tab.uploads.length === 0 ? (
                          <div className="flex items-center justify-between rounded-xl border border-dashed border-slate-200/70 bg-slate-50 px-4 py-6 text-sm text-muted-foreground dark:border-slate-800/60 dark:bg-slate-900/60">
                            <span>{t("prototype.connectors.table.empty", { defaultValue: "No uploads recorded yet." })}</span>
                            <ShieldCheck className="h-5 w-5 text-emerald-500" aria-hidden="true" />
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>{t("prototype.connectors.table.batch", { defaultValue: "Batch" })}</TableHead>
                                  <TableHead>{t("prototype.connectors.table.status", { defaultValue: "Status" })}</TableHead>
                                  <TableHead>{t("prototype.connectors.table.accepted", { defaultValue: "Accepted" })}</TableHead>
                                  <TableHead>{t("prototype.connectors.table.matchRate", { defaultValue: "Match rate" })}</TableHead>
                                  <TableHead>{t("prototype.connectors.table.revenue", { defaultValue: "Revenue" })}</TableHead>
                                  <TableHead>{t("prototype.connectors.table.notes", { defaultValue: "Notes" })}</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {tab.uploads.map((row) => (
                                  <TableRow key={`${tab.value}-${row.id}`}>
                                    <TableCell className="font-medium text-foreground">
                                      <div className="flex flex-col">
                                        <span>{row.batchId}</span>
                                        <span className="text-xs text-muted-foreground">{row.relativeTime}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge className={cn("text-xs font-medium", statusStyles[row.status])}>{row.statusText}</Badge>
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{row.conversions}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{row.matchRate}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{row.revenue}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{row.note}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>

                      <div className="space-y-3 rounded-2xl border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800/60 dark:bg-slate-900/50">
                        <div className="flex flex-col gap-1 pb-2 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />
                            <div>
                              <p className="text-sm font-semibold text-foreground">
                                {t("prototype.connectors.dlq.title", { defaultValue: "Dead-letter queue" })}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {t("prototype.connectors.dlq.subtitle", {
                                  defaultValue: "Resolve failing batches, then replay to clear the queue.",
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {dlqRefreshing ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            ) : null}
                            <Badge variant="outline" className="text-xs font-medium">
                              {t("prototype.connectors.dlq.count", {
                                defaultValue: "{{count}} waiting",
                                count: formatNumber(tab.dlqUploads?.length ?? 0),
                              })}
                            </Badge>
                          </div>
                        </div>
                        {tab.dlqUploads && tab.dlqUploads.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>{t("prototype.connectors.table.batch", { defaultValue: "Batch" })}</TableHead>
                                  <TableHead>{t("prototype.connectors.dlq.rows", { defaultValue: "Rows" })}</TableHead>
                                  <TableHead>{t("prototype.connectors.dlq.reason", { defaultValue: "Reason" })}</TableHead>
                                  <TableHead>{t("prototype.connectors.dlq.recorded", { defaultValue: "Recorded" })}</TableHead>
                                  <TableHead className="text-right">
                                    {t("prototype.connectors.dlq.actions", { defaultValue: "Actions" })}
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {tab.dlqUploads.map((row) => (
                                  <TableRow key={`${tab.value}-dlq-${row.id}`}>
                                    <TableCell className="font-medium text-foreground">
                                      <div className="flex flex-col">
                                        <span>{row.batchId}</span>
                                        <span className="text-xs text-muted-foreground">{row.relativeTime}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{row.rowsSummary}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{row.reason}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{row.relativeTime}</TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-xs"
                                        onClick={() => handleReplayUpload(row.id)}
                                        disabled={replayUploadMutation.isPending}
                                      >
                                        {replayUploadMutation.isPending && replayUploadMutation.variables?.uploadId === row.id ? (
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                        ) : (
                                          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                                        )}
                                        {t("prototype.connectors.dlq.replay", { defaultValue: "Replay" })}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="rounded-xl border border-dashed border-slate-200/70 bg-slate-50 px-4 py-6 text-sm text-muted-foreground dark:border-slate-800/60 dark:bg-slate-900/60">
                            {t("prototype.connectors.dlq.empty", { defaultValue: "No batches waiting in DLQ." })}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-3 px-2">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-foreground">
                            {t("prototype.connectors.queue.heading", { defaultValue: "Upload queue" })}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t("prototype.connectors.queue.subtitle", {
                              defaultValue: "Merged view across all connectors.",
                            })}
                          </span>
                        </div>
                        <Badge className={cn("text-xs font-medium", statusStyles[tab.status])}>{tab.health}</Badge>
                      </div>
                      {tab.uploadsLoading ? (
                        <Skeleton className="h-48 w-full rounded-xl" />
                      ) : tab.uploads.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-200/70 bg-slate-50 px-4 py-6 text-sm text-muted-foreground dark:border-slate-800/60 dark:bg-slate-900/60">
                          {t("prototype.connectors.queue.empty", { defaultValue: "Queue is clear." })}
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>{t("prototype.connectors.table.batch", { defaultValue: "Batch" })}</TableHead>
                                <TableHead>{t("prototype.connectors.table.connector", { defaultValue: "Connector" })}</TableHead>
                                <TableHead>{t("prototype.connectors.table.status", { defaultValue: "Status" })}</TableHead>
                                <TableHead>{t("prototype.connectors.table.accepted", { defaultValue: "Accepted" })}</TableHead>
                                <TableHead>{t("prototype.connectors.table.matchRate", { defaultValue: "Match rate" })}</TableHead>
                                <TableHead>{t("prototype.connectors.table.revenue", { defaultValue: "Revenue" })}</TableHead>
                                <TableHead>{t("prototype.connectors.table.notes", { defaultValue: "Notes" })}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {tab.uploads.map((row) => (
                                <TableRow key={`${tab.value}-${row.id}`}>
                                  <TableCell className="font-medium text-foreground">
                                    <div className="flex flex-col">
                                      <span>{row.batchId}</span>
                                      <span className="text-xs text-muted-foreground">{row.relativeTime}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{row.connectorLabel}</TableCell>
                                  <TableCell>
                                    <Badge className={cn("text-xs font-medium", statusStyles[row.status])}>{row.statusText}</Badge>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{row.conversions}</TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{row.matchRate}</TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{row.revenue}</TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{row.note}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </section>
        <section className="flex flex-col gap-6">
          <AutopilotOnboardingWizard
            diagnostics={diagnostics}
            onRunCanary={() => runCanaryMutation.mutateAsync()}
            onToggleConnector={(connector, enabled) => handleToggleConnector(connector, "sandbox", enabled)}
            onConfigureConnector={(connector) => setCredentialDialogConnector(connector)}
            toggleState={{
              isPending: toggleConnectorMutation.isPending,
              pendingConnector: pendingToggleConnector,
            }}
            isDiagnosticsLoading={diagnosticsLoading}
            isRunningCanary={runCanaryMutation.isPending}
          />
          <AutopilotNotificationCreditsCard />
        </section>
      </div>
      <AutopilotConnectorCredentialDialog
        connector={credentialDialogConnector}
        open={credentialDialogConnector !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCredentialDialogConnector(null);
          }
        }}
        environment="production"
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ["autopilot", "credentials"] });
          void refetchDiagnostics();
        }}
      />
    </DashboardLayoutV3>
  );
};

export default AutopilotPage;
